import { SPIRIT_STONE_CLASS_CONFIGS } from "@xiuxian/game-core";
import type { ServerSnapshotMessage } from "@xiuxian/game-protocol";
import Phaser from "phaser";
import {
  FORMATION_REALM_KEYS,
  type FormationRealmKey,
  getFormationLayerLimit,
  getFormationRealmKey,
} from "../formation-rendering.js";
import {
  type AttackFormationStyle,
  getAttackFormationStyle,
} from "../formation-ui.js";
import {
  getSpiritStoneVisualStyle,
  type SpiritStoneVisualStyle,
} from "../growth-ui.js";
import { formatDefaultPlayerName } from "../i18n/game-formatters.js";
import {
  formatControlHint,
  formatDebugToggleLog,
} from "../i18n/hud-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "../i18n/index.js";
import {
  getMinimapHeatmapStyle,
  getMinimapLeaderboardMarkerStyle,
  getMinimapStoneStyle,
  type MinimapLayout,
  worldToMinimapPoint,
} from "../minimap-ui.js";
import type { MobileControlsInputSource } from "../mobile-controls.js";
import {
  getRuntimeCameraZoom,
  readMobileRuntimeSnapshot,
} from "../mobile-runtime.js";
import type { RealtimeClient } from "../realtime-client.js";
import { readRuntimePerformanceHudEnabled } from "../runtime-env.js";
import type {
  AttackEffect,
  AudioCue,
  AudioCueKind,
  DropBurstEffect,
  PickupEffect,
  RuntimeStateStore,
  RuntimeViewState,
} from "../runtime-state.js";
import {
  getStaminaBarStyle,
  getStaminaRatio,
  STAMINA_LOW_RATIO,
  smoothStaminaRatio,
} from "../stamina-ui.js";
import {
  getTreasureTextureKey,
  getTreasureUiConfig,
  TREASURE_KINDS,
} from "../treasure-ui.js";
import {
  DEFAULT_SKIN_ID,
  resolveSkinDef,
  SKIN_DEFS,
  type SkinId,
} from "../visual/cultivator-skins.js";
import {
  DEFAULT_TERRAIN_PATTERN,
  ensureTerrainPatternTexture,
  normalizeTerrainPattern,
  type TerrainPatternId,
} from "../visual/terrain-patterns.js";
import {
  drawSimpleWorldBoundary,
  drawSimpleWorldGround,
} from "../visual/world-background.js";

const PANEL_STROKE = 0xb99146;
// Spirit stones now come in just two tiers, drawn flat (no per-frame style
// recompute, LOD modes, aura or pulse). Precompute one style per tier: a small
// green fragment vs a larger cyan condensed gem — cheap and clearly distinct.
const CONDENSED_STONE_MIN_VALUE = SPIRIT_STONE_CLASS_CONFIGS.condensed.value;
const FRAGMENT_STONE_STYLE = getSpiritStoneVisualStyle(
  SPIRIT_STONE_CLASS_CONFIGS.fragment.value,
);
const CONDENSED_STONE_STYLE = getSpiritStoneVisualStyle(
  SPIRIT_STONE_CLASS_CONFIGS.condensed.value,
);
const INPUT_SEND_INTERVAL_MS = 50;
const ATTACK_ARC_SEGMENTS = 28;
const ATTACK_RUNE_MAX_COUNT = 28;
const ATTACK_SECTOR_INNER_ARC_RATIOS = [0.46, 0.62, 0.78] as const;
const FORMATION_LAYER_RING_COUNT = 5;
const TEXTURED_FORMATION_SOURCE_RADIUS = 430;
const TEXTURED_FORMATION_LAYER_DEFS = [
  {
    suffix: "base",
    depth: 3.05,
    alpha: 0.8,
  },
  {
    suffix: "elements",
    depth: 3.1,
    alpha: 0.9,
  },
  {
    suffix: "runes",
    depth: 3.15,
    alpha: 0.78,
  },
] as const;
const MAX_TEXTURED_FORMATION_LAYERS = TEXTURED_FORMATION_LAYER_DEFS.length;
const ENABLE_PERFORMANCE_DEBUG = readRuntimePerformanceHudEnabled(
  import.meta.env.VITE_RUNTIME_PERFORMANCE_HUD_ENABLED,
  import.meta.env.DEV,
);
const FLYING_SWORD_TEXTURE_KEY = "flying-sword-placeholder";
const FLYING_SWORD_TEXTURE_PATH = "/vfx/flying-sword/flying-sword-basic.png";
const FLYING_SWORD_TEXTURE_SIZE = 96;
const FLYING_SWORD_DISPLAY_SCALE = 0.5;
const KEYBOARD_STEER_TARGET_OFFSET = Math.PI / 2;
const POINTER_MODE_MOVE_THRESHOLD = 8;
const LOCKED_CURSOR_RADIUS = 96;
const LOCKED_CURSOR_MIN_DISTANCE = 12;
const PLAYER_LABEL_OFFSET = 12;
const SHOW_STAMINA_BAR = true;
const STAMINA_BAR_WIDTH = 64;
const STAMINA_BAR_HEIGHT = 5;
const STAMINA_BAR_OFFSET = 22;
const STAMINA_FEEDBACK_MS = 260;
const PICKUP_EFFECT_MS = 420;
const DROP_BURST_EFFECT_MS = 820;
const ATTACK_EFFECT_MAX_MS = 900;
const MOBILE_PICKUP_EFFECT_LIMIT = 56;
const MOBILE_DROP_BURST_EFFECT_LIMIT = 8;
const MOBILE_ATTACK_EFFECT_LIMIT = 8;
const MOBILE_FAR_FORMATION_MIN_DISTANCE = 520;
const DESKTOP_FAR_FORMATION_MIN_DISTANCE = 900;
const SWORD_SLASH_MS = 180;
const LOW_TIER_ATTACK_LEVEL_MAX = 10;
const SWORD_SLASH_VISUAL_MIN_MS = 220;
const SWORD_LOW_TIER_SLASH_VISUAL_MIN_MS = 280;
const SWORD_SLASH_CASCADE_STEP_MS = 42;
const SWORD_SLASH_CASCADE_MAX_MS = 260;
const SWORD_SLASH_TRAVEL_MS = 180;
const SWORD_MISS_RETURN_MS = 360;
const SWORDS_PER_LAUNCH_RING = 2;
const MINIMAP_SIZE = 172;
const MINIMAP_PADDING = 22;
const MINIMAP_MIN_SIZE = 96;
const MOBILE_MINIMAP_SIZE = 136;
const MOBILE_MINIMAP_PADDING = 14;
const MOBILE_MINIMAP_MIN_SIZE = 104;
const MOBILE_LANDSCAPE_MINIMAP_TOP_RESERVED = 52;
const MINIMAP_RADIUS = 72;
const MINIMAP_BOTTOM_RESERVED = 48;
const TREASURE_WORLD_DISPLAY_SIZE = 62;
const TREASURE_MINIMAP_RADIUS = 5.2;
const CULTIVATOR_BASE_SIZE = 34;
const CULTIVATOR_LEVEL_SIZE_STEP = 3;
const CULTIVATOR_ELLIPSE_SEGMENTS = 18;
const CULTIVATOR_ANIMATION_FRAME_RATE = 7;
const CULTIVATOR_BOOST_ANIMATION_FRAME_RATE = 12;
const CULTIVATOR_SPRITE_FRAME_SIZE = 256;
const CULTIVATOR_SPRITE_BASE_SIZE = 76;
const CULTIVATOR_SPRITE_LEVEL_SIZE_STEP = 5;
const SPIRIT_REALM_MUSIC_KEY = "bgm-spirit-realm-loop";
const SPIRIT_REALM_MUSIC_PATH = "/audio/music/bgm_spirit_realm_loop.ogg";
const SPIRIT_REALM_MUSIC_VOLUME = 0.36;
const SPIRIT_REALM_MUSIC_DEAD_VOLUME = 0;
const SPIRIT_REALM_MUSIC_FADE_MS = 2000;
const AUDIO_CUE_MAX_AGE_MS = 4000;
const AUDIO_FOCUS_RESUME_SUPPRESS_MS = 650;
const SOFT_BUMP_COOLDOWN_MS = 450;
const UI_AUDIO_CUES = new Set<AudioCueKind>([
  "ui_focus",
  "ui_start",
  "ui_reincarnate",
  "ui_error",
  "treasure_spawned",
  "treasure_acquired",
]);
const REINCARNATION_PAGE_AUDIO_CUES = new Set<AudioCueKind>([
  ...UI_AUDIO_CUES,
  "reincarnation_fade",
]);
const REINCARNATION_STINGER_KEY: AudioCueKind = "reincarnation_fade";
const SFX_ASSETS: Array<{ key: AudioCueKind; path: string; volume: number }> = [
  { key: "ui_focus", path: "/audio/sfx/ui_focus_jade_tap.ogg", volume: 0.42 },
  { key: "ui_start", path: "/audio/sfx/ui_start_game.ogg", volume: 0.58 },
  {
    key: "ui_reincarnate",
    path: "/audio/sfx/ui_reincarnate.ogg",
    volume: 0.52,
  },
  { key: "ui_error", path: "/audio/sfx/ui_error.ogg", volume: 0.5 },
  {
    key: "pickup_low",
    path: "/audio/sfx/pickup_stone_low_01.ogg",
    volume: 0.14,
  },
  {
    key: "pickup_mid",
    path: "/audio/sfx/pickup_stone_mid_01.ogg",
    volume: 0.17,
  },
  {
    key: "pickup_high",
    path: "/audio/sfx/pickup_stone_high_01.ogg",
    volume: 0.22,
  },
  {
    key: "treasure_spawned",
    path: "/audio/sfx/treasure_spawned.ogg",
    volume: 0.78,
  },
  {
    key: "treasure_acquired",
    path: "/audio/sfx/treasure_acquired.ogg",
    volume: 0.68,
  },
  {
    key: "stone_scatter",
    path: "/audio/sfx/world_stone_scatter.ogg",
    volume: 0.5,
  },
  { key: "level_up", path: "/audio/sfx/growth_level_up.ogg", volume: 0.7 },
  {
    key: "sword_windup",
    path: "/audio/sfx/combat_sword_windup.ogg",
    volume: 0.22,
  },
  {
    key: "sword_release",
    path: "/audio/sfx/combat_sword_release.ogg",
    volume: 0.24,
  },
  { key: "sword_hit", path: "/audio/sfx/combat_sword_hit.ogg", volume: 0.28 },
  { key: "sword_miss", path: "/audio/sfx/combat_sword_miss.ogg", volume: 0.2 },
  {
    key: "soft_bump",
    path: "/audio/sfx/character_soft_bump.ogg",
    volume: 0.224,
  },
  {
    key: "death_scatter",
    path: "/audio/sfx/world_death_scatter.ogg",
    volume: 0.62,
  },
  {
    key: "reincarnation_fade",
    path: "/audio/sfx/world_reincarnation_fade_out.ogg",
    volume: 0.55,
  },
];
const SFX_BY_KEY = new Map(SFX_ASSETS.map((asset) => [asset.key, asset]));
type ControlMode = "mouse" | "keyboard" | "mobile";
type MouseLockStatusListener = (active: boolean, preferred: boolean) => void;
type VolumeControllableSound = Phaser.Sound.BaseSound & {
  setVolume(value: number): Phaser.Sound.BaseSound;
};
type FlyingSwordTone = "windup" | "slash";

interface FlyingSwordVisualStyle {
  x: number;
  y: number;
  angle: number;
  length: number;
  alpha: number;
  tone: FlyingSwordTone;
  bright: boolean;
}

interface StoneRenderStats {
  simple: number;
  medium: number;
  full: number;
}

export class RuntimeScene extends Phaser.Scene {
  private background?: Phaser.GameObjects.Graphics;
  private worldGroundLayer?: Phaser.GameObjects.Graphics;
  private terrainTileSprite?: Phaser.GameObjects.TileSprite;
  private terrainMaskGraphics?: Phaser.GameObjects.Graphics;
  private terrainPattern: TerrainPatternId = DEFAULT_TERRAIN_PATTERN;
  private selfSkinId: SkinId = DEFAULT_SKIN_ID;
  private worldLayer?: Phaser.GameObjects.Graphics;
  private minimapLayer?: Phaser.GameObjects.Graphics;
  private lockedCursorLayer?: Phaser.GameObjects.Graphics;
  private hintText?: Phaser.GameObjects.Text;
  private performanceText?: Phaser.GameObjects.Text;
  private readonly playerLabels = new Map<string, Phaser.GameObjects.Text>();
  private readonly texturedFormationSprites = new Map<
    string,
    Phaser.GameObjects.Image
  >();
  private readonly playerSprites = new Map<string, Phaser.GameObjects.Sprite>();
  private readonly swordSprites: Phaser.GameObjects.Image[] = [];
  private treasureSprite?: Phaser.GameObjects.Image;
  private minimapTreasureSprite?: Phaser.GameObjects.Image;
  private swordSpriteCursor = 0;
  private unsubscribe: (() => void) | null = null;
  private viewState: RuntimeViewState | null = null;
  private boostKey?: Phaser.Input.Keyboard.Key;
  private leftKey?: Phaser.Input.Keyboard.Key;
  private rightKey?: Phaser.Input.Keyboard.Key;
  private mouseLockKey?: Phaser.Input.Keyboard.Key;
  private escapeKey?: Phaser.Input.Keyboard.Key;
  private debugToggleKeys: Phaser.Input.Keyboard.Key[] = [];
  private debugModifierKey?: Phaser.Input.Keyboard.Key;
  private nextInputAt = 0;
  private lastSentAngle = 0;
  private lastBoost = false;
  private controlMode: ControlMode = "mouse";
  private previousKeyboardTurnDirection = 0;
  private keyboardLockedAngle: number | null = null;
  private mouseLockActive = false;
  private mouseLockPreferred = false;
  private mouseLockRestorePending = false;
  private lockedCursorPosition: { x: number; y: number } | null = null;
  private lastPointerPosition: { x: number; y: number } | null = null;
  private lastSelfLevel: number | null = null;
  private displayedStaminaRatio: number | null = null;
  private staminaFeedbackUntil = 0;
  private nextPerformanceSampleAt = 0;
  private longFrames16 = 0;
  private longFrames25 = 0;
  private longFrames33 = 0;
  private lastPerformanceRateAt = 0;
  private lastPerformanceMessageCount = 0;
  private debugFormationLayerLimit: number = MAX_TEXTURED_FORMATION_LAYERS;
  private debugSimpleBackground = false;
  private debugSimpleStones = false;
  private debugMinimapVisible = true;
  private debugPlayerSpritesVisible = true;
  private stoneRenderStats = createEmptyStoneRenderStats();
  private spiritRealmMusic: VolumeControllableSound | null = null;
  private spiritRealmMusicVolume = SPIRIT_REALM_MUSIC_VOLUME;
  private shouldStartSpiritRealmMusic = false;
  private audioMuted = false;
  private readonly playedAudioCueIds = new Set<string>();
  private readonly mouseLockStatusListeners =
    new Set<MouseLockStatusListener>();
  private lastSoftBumpAt = 0;
  private audioCueSuppressedUntil = 0;
  private readonly handlePointerLockChange = (): void => {
    this.syncMouseLockState();
  };
  private readonly handleLockedMouseMove = (event: MouseEvent): void => {
    if (!this.mouseLockActive) {
      return;
    }
    this.moveLockedCursor(event.movementX, event.movementY);
  };
  private readonly handleWindowAudioFocus = () => {
    this.suppressTransientAudioAfterFocusResume();
  };
  private readonly handleWindowAudioBlur = () => {
    this.suppressTransientAudioAfterFocusResume();
  };
  private readonly handleVisibilityAudioResume = () => {
    if (document.visibilityState === "visible") {
      this.suppressTransientAudioAfterFocusResume();
    }
  };

  constructor(
    private readonly store: RuntimeStateStore,
    private readonly realtimeClient: RealtimeClient,
    private readonly mobileControls?: MobileControlsInputSource,
    private readonly getLocale: () => Locale = () => DEFAULT_LOCALE,
  ) {
    super("runtime-scene");
  }

  preload(): void {
    this.load.image(FLYING_SWORD_TEXTURE_KEY, FLYING_SWORD_TEXTURE_PATH);
    for (const kind of TREASURE_KINDS) {
      this.load.image(
        getTreasureTextureKey(kind),
        getTreasureUiConfig(kind).iconPath,
      );
    }
    for (const realmKey of FORMATION_REALM_KEYS) {
      for (const [index, layer] of TEXTURED_FORMATION_LAYER_DEFS.entries()) {
        this.load.image(
          createTexturedFormationTextureKey(realmKey, index),
          `/formation/base/${realmKey}-layer-${(index + 1).toString()}-${layer.suffix}.png`,
        );
      }
    }
    this.load.audio(SPIRIT_REALM_MUSIC_KEY, SPIRIT_REALM_MUSIC_PATH);
    for (const asset of SFX_ASSETS) {
      this.load.audio(asset.key, asset.path);
    }
    for (const def of SKIN_DEFS) {
      this.load.spritesheet(def.sheetKey, def.path, {
        frameWidth: CULTIVATOR_SPRITE_FRAME_SIZE,
        frameHeight: CULTIVATOR_SPRITE_FRAME_SIZE,
      });
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#05070f");
    this.cameras.main.setRoundPixels(true);
    this.applyRuntimeCameraZoom();
    this.sound.setMute(this.audioMuted);

    this.background = this.add.graphics().setDepth(-30).setScrollFactor(0);
    this.worldGroundLayer = this.add.graphics().setDepth(-21);
    this.terrainTileSprite = this.add
      .tileSprite(0, 0, 2, 2, "__WHITE")
      .setOrigin(0.5, 0.5)
      .setDepth(-18)
      .setVisible(false);
    this.terrainMaskGraphics = this.make.graphics({ x: 0, y: 0 }, false);
    this.terrainTileSprite.setMask(
      this.terrainMaskGraphics.createGeometryMask(),
    );
    this.worldLayer = this.add.graphics().setDepth(-10);
    this.minimapLayer = this.add.graphics().setDepth(19).setScrollFactor(0);
    this.lockedCursorLayer = this.add
      .graphics()
      .setDepth(22)
      .setScrollFactor(0);
    this.createCultivatorAnimations();
    this.createFlyingSwordPlaceholderTexture();
    this.hintText = this.add
      .text(24, 0, "", {
        color: "#8fa0bf",
        fontFamily:
          "'Avenir Next', 'Segoe UI', 'PingFang SC', 'Noto Sans SC', sans-serif",
        fontSize: "13px",
      })
      .setDepth(20)
      .setScrollFactor(0);
    this.performanceText = this.add
      .text(24, 236, "", {
        color: "#d7f6ff",
        fontFamily:
          "'Avenir Next', 'Segoe UI', 'PingFang SC', 'Noto Sans SC', sans-serif",
        fontSize: "12px",
        lineSpacing: 3,
        stroke: "#07101d",
        strokeThickness: 3,
      })
      .setDepth(21)
      .setScrollFactor(0);

    this.boostKey = this.input.keyboard?.addKey(
      Phaser.Input.Keyboard.KeyCodes.SHIFT,
    );
    this.leftKey = this.input.keyboard?.addKey(
      Phaser.Input.Keyboard.KeyCodes.LEFT,
    );
    this.rightKey = this.input.keyboard?.addKey(
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
    );
    this.mouseLockKey = this.input.keyboard?.addKey(
      Phaser.Input.Keyboard.KeyCodes.M,
    );
    this.escapeKey = this.input.keyboard?.addKey(
      Phaser.Input.Keyboard.KeyCodes.ESC,
    );
    if (ENABLE_PERFORMANCE_DEBUG) {
      this.debugToggleKeys = [
        Phaser.Input.Keyboard.KeyCodes.ONE,
        Phaser.Input.Keyboard.KeyCodes.TWO,
        Phaser.Input.Keyboard.KeyCodes.THREE,
        Phaser.Input.Keyboard.KeyCodes.FOUR,
        Phaser.Input.Keyboard.KeyCodes.FIVE,
      ]
        .map((keyCode) => this.input.keyboard?.addKey(keyCode))
        .filter((key): key is Phaser.Input.Keyboard.Key => Boolean(key));
      // Require a held modifier so the debug toggles cannot be triggered by a
      // stray number-key press during play. Backtick is unused in gameplay and
      // is not intercepted by the browser the way Alt/Ctrl are.
      this.debugModifierKey = this.input.keyboard?.addKey(
        Phaser.Input.Keyboard.KeyCodes.BACKTICK,
      );
    }
    document.addEventListener(
      "pointerlockchange",
      this.handlePointerLockChange,
    );
    document.addEventListener("mousemove", this.handleLockedMouseMove);
    this.scale.on("resize", () => {
      this.applyRuntimeCameraZoom();
      this.drawBackdrop();
      this.updateHint();
      this.clampLockedCursor();
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = null;
      document.removeEventListener(
        "pointerlockchange",
        this.handlePointerLockChange,
      );
      document.removeEventListener("mousemove", this.handleLockedMouseMove);
      this.exitMouseLock();
      this.clearPlayerLabels();
      this.clearPlayerSprites();
      this.removeAudioFocusHandlers();
      this.stopSpiritRealmMusic();
    });
    this.installAudioFocusHandlers();

    this.unsubscribe = this.store.subscribe((state) => {
      this.viewState = state;
      this.drawBackdrop();
      this.updateHint();
    });
  }

  startSpiritRealmMusic(): void {
    this.shouldStartSpiritRealmMusic = true;
    if (this.sound.locked) {
      this.sound.once(Phaser.Sound.Events.UNLOCKED, () => {
        this.playSpiritRealmMusic();
      });
      return;
    }

    this.playSpiritRealmMusic();
  }

  setAudioMuted(muted: boolean): void {
    this.audioMuted = muted;
    const sound = this.sound as typeof this.sound | undefined;
    sound?.setMute(muted);
  }

  onMouseLockStatusChange(listener: MouseLockStatusListener): () => void {
    this.mouseLockStatusListeners.add(listener);
    listener(this.mouseLockActive, this.mouseLockPreferred);
    return () => {
      this.mouseLockStatusListeners.delete(listener);
    };
  }

  toggleMouseLockFromUi(): void {
    if (this.mouseLockActive) {
      this.exitMouseLock();
      return;
    }

    const self = this.viewState?.lastSnapshot?.self;
    if (!self?.isAlive) {
      return;
    }
    this.enterMouseLock(self.facing);
  }

  restoreMouseLockAfterRespawn(): void {
    if (!this.mouseLockPreferred) {
      return;
    }
    this.enterMouseLock(
      this.viewState?.lastSnapshot?.self?.facing ?? this.lastSentAngle,
      true,
    );
  }

  pauseMouseLockForIdentityPrompt(): void {
    if (!this.game?.canvas) {
      return;
    }
    this.exitMouseLock(true);
  }

  update(time: number, delta = 0): void {
    const updateStartedAt = performance.now();
    if (delta > 16.7) {
      this.longFrames16 += 1;
    }
    if (delta > 25) {
      this.longFrames25 += 1;
    }
    if (delta > 33.4) {
      this.longFrames33 += 1;
    }

    this.handleControlShortcuts();
    const drawStartedAt = performance.now();
    this.drawWorld(time, delta);
    const drawWorldMs = performance.now() - drawStartedAt;
    this.drawLockedCursor();
    this.playSpiritRealmMusic();
    this.updateSpiritRealmMusicVolume(delta);
    this.playPendingAudioCues();
    this.sendInputIfReady(time);
    this.recordPerformanceSample(time, {
      drawWorldMs,
      frameDeltaMs: delta,
      updateMs: performance.now() - updateStartedAt,
    });
  }

  private playSpiritRealmMusic(): void {
    if (!this.shouldStartSpiritRealmMusic || this.spiritRealmMusic?.isPlaying) {
      return;
    }
    if (!this.cache.audio.exists(SPIRIT_REALM_MUSIC_KEY)) {
      return;
    }

    this.spiritRealmMusic ??= this.sound.add(SPIRIT_REALM_MUSIC_KEY, {
      loop: true,
      volume: this.spiritRealmMusicVolume,
    }) as VolumeControllableSound;
    this.spiritRealmMusic.play();
  }

  private updateSpiritRealmMusicVolume(delta: number): void {
    if (!this.spiritRealmMusic?.isPlaying) {
      return;
    }

    const targetVolume = this.getSpiritRealmMusicTargetVolume();
    const distance = targetVolume - this.spiritRealmMusicVolume;
    const fadeStep =
      ((SPIRIT_REALM_MUSIC_VOLUME - SPIRIT_REALM_MUSIC_DEAD_VOLUME) *
        Math.max(0, delta)) /
      SPIRIT_REALM_MUSIC_FADE_MS;
    if (Math.abs(distance) <= fadeStep || fadeStep === 0) {
      this.spiritRealmMusicVolume = targetVolume;
    } else {
      this.spiritRealmMusicVolume += Math.sign(distance) * fadeStep;
    }
    this.spiritRealmMusic.setVolume(this.spiritRealmMusicVolume);
  }

  private getSpiritRealmMusicTargetVolume(): number {
    return this.viewState?.lastSnapshot?.self?.isAlive === false
      ? SPIRIT_REALM_MUSIC_DEAD_VOLUME
      : SPIRIT_REALM_MUSIC_VOLUME;
  }

  private stopSpiritRealmMusic(): void {
    this.shouldStartSpiritRealmMusic = false;
    this.spiritRealmMusic?.stop();
    this.sound.removeByKey(SPIRIT_REALM_MUSIC_KEY);
    this.spiritRealmMusic = null;
  }

  private playPendingAudioCues(): void {
    if (!this.viewState) {
      return;
    }

    const now = Date.now();
    if (now < this.audioCueSuppressedUntil) {
      this.discardPendingAudioCues(now);
      return;
    }
    for (const cue of [...this.viewState.audioCues].reverse()) {
      if (
        this.playedAudioCueIds.has(cue.id) ||
        now - cue.createdAt > AUDIO_CUE_MAX_AGE_MS
      ) {
        continue;
      }
      if (this.shouldPlayAudioCue(cue)) {
        this.playAudioCue(cue);
      }
      this.playedAudioCueIds.add(cue.id);
    }

    this.store.pruneAudioCues(now - AUDIO_CUE_MAX_AGE_MS);
    if (this.playedAudioCueIds.size > 128) {
      this.playedAudioCueIds.clear();
    }
  }

  private playAudioCue(cue: AudioCue): void {
    this.playAudioCueKind(cue.kind, cue.volumeScale, cue.detune);
  }

  private shouldPlayAudioCue(cue: AudioCue): boolean {
    const self = this.viewState?.lastSnapshot?.self;
    if (
      self?.isAlive === false &&
      !REINCARNATION_PAGE_AUDIO_CUES.has(cue.kind)
    ) {
      return false;
    }
    return true;
  }

  private playAudioCueKind(
    kind: AudioCueKind,
    volumeScale = 1,
    detune = 0,
  ): void {
    if (this.sound.locked || Date.now() < this.audioCueSuppressedUntil) {
      return;
    }
    const asset = SFX_BY_KEY.get(kind);
    if (!asset) {
      return;
    }
    if (!this.cache.audio.exists(kind)) {
      return;
    }
    if (kind === REINCARNATION_STINGER_KEY) {
      this.stopReincarnationStinger();
      this.silenceSpiritRealmMusic();
    }
    if (kind === "ui_reincarnate" || kind === "ui_start") {
      this.stopReincarnationStinger();
    }
    this.sound.play(kind, {
      volume: Phaser.Math.Clamp(asset.volume * volumeScale, 0, 1),
      detune,
    });
  }

  private silenceSpiritRealmMusic(): void {
    if (!this.spiritRealmMusic?.isPlaying) {
      return;
    }
    this.spiritRealmMusicVolume = SPIRIT_REALM_MUSIC_DEAD_VOLUME;
    this.spiritRealmMusic.setVolume(this.spiritRealmMusicVolume);
  }

  private stopReincarnationStinger(): void {
    this.sound.stopByKey(REINCARNATION_STINGER_KEY);
  }

  private installAudioFocusHandlers(): void {
    window.addEventListener("focus", this.handleWindowAudioFocus);
    window.addEventListener("blur", this.handleWindowAudioBlur);
    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityAudioResume,
    );
  }

  private removeAudioFocusHandlers(): void {
    window.removeEventListener("focus", this.handleWindowAudioFocus);
    window.removeEventListener("blur", this.handleWindowAudioBlur);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityAudioResume,
    );
  }

  private suppressTransientAudioAfterFocusResume(): void {
    const now = Date.now();
    this.audioCueSuppressedUntil = Math.max(
      this.audioCueSuppressedUntil,
      now + AUDIO_FOCUS_RESUME_SUPPRESS_MS,
    );
    this.stopTransientSfx();
    this.discardPendingAudioCues(now);
  }

  private stopTransientSfx(): void {
    for (const asset of SFX_ASSETS) {
      this.sound.stopByKey(asset.key);
    }
  }

  private discardPendingAudioCues(now: number): void {
    if (!this.viewState) {
      return;
    }
    for (const cue of this.viewState.audioCues) {
      this.playedAudioCueIds.add(cue.id);
    }
    this.store.pruneAudioCues(now);
  }

  private recordPerformanceSample(
    time: number,
    sample: { drawWorldMs: number; frameDeltaMs: number; updateMs: number },
  ): void {
    if (!this.viewState || time < this.nextPerformanceSampleAt) {
      return;
    }
    this.nextPerformanceSampleAt = time + 250;
    const snapshot = this.viewState.lastSnapshot;
    const fps = this.game.loop.actualFps;
    const messageRateElapsedMs =
      this.lastPerformanceRateAt > 0 ? time - this.lastPerformanceRateAt : 0;
    const messageCount = this.store.getState().receivedMessageCount;
    const messagesPerSecond =
      messageRateElapsedMs > 0
        ? ((messageCount - this.lastPerformanceMessageCount) * 1000) /
          messageRateElapsedMs
        : 0;
    this.lastPerformanceRateAt = time;
    this.lastPerformanceMessageCount = messageCount;
    this.store.recordPerformanceSample({
      fps: Number.isFinite(fps) ? fps : 0,
      updateMs: sample.updateMs,
      drawWorldMs: sample.drawWorldMs,
      frameDeltaMs: sample.frameDeltaMs,
      longFrames16: this.longFrames16,
      longFrames25: this.longFrames25,
      longFrames33: this.longFrames33,
      visiblePlayers: snapshot ? snapshot.visiblePlayers.length : 0,
      visibleStones: snapshot ? snapshot.visibleStones.length : 0,
      stoneSimple: this.stoneRenderStats.simple,
      stoneMedium: this.stoneRenderStats.medium,
      stoneFull: this.stoneRenderStats.full,
      pickupEffects: this.viewState.pickupEffects.length,
      attackEffects: this.viewState.attackEffects.length,
      displayObjects: this.children.length,
      playerSprites: countVisibleGameObjects(this.playerSprites.values()),
      formationSprites: countVisibleGameObjects(
        this.texturedFormationSprites.values(),
      ),
      swordSprites: countVisibleGameObjects(this.swordSprites),
      messagesPerSecond,
      heapUsedMb: readHeapUsedMb(),
    });
    this.updatePerformanceText(this.store.getState().performance);
  }

  private updatePerformanceText(
    performanceSnapshot: RuntimeViewState["performance"],
  ): void {
    if (!this.performanceText) {
      return;
    }
    if (!ENABLE_PERFORMANCE_DEBUG || this.mobileControls?.isMobileEnabled()) {
      this.performanceText.setVisible(false);
      return;
    }
    this.performanceText.setVisible(true);
    this.performanceText.setText(
      [
        `FPS ${performanceSnapshot.fps.toFixed(0)}  upd ${performanceSnapshot.updateMs.toFixed(2)}ms avg ${performanceSnapshot.updateMsAverage.toFixed(2)}ms`,
        `draw ${performanceSnapshot.drawWorldMs.toFixed(2)}ms avg ${performanceSnapshot.drawWorldMsAverage.toFixed(2)}ms`,
        `delta ${performanceSnapshot.frameDeltaMs.toFixed(1)}ms avg ${performanceSnapshot.frameDeltaMsAverage.toFixed(1)}ms long ${performanceSnapshot.longFrames16.toString()}/${performanceSnapshot.longFrames25.toString()}/${performanceSnapshot.longFrames33.toString()}`,
        `visible P/S ${performanceSnapshot.visiblePlayers.toString()}/${performanceSnapshot.visibleStones.toString()}`,
        `stone LOD s/m/f ${performanceSnapshot.stoneSimple.toString()}/${performanceSnapshot.stoneMedium.toString()}/${performanceSnapshot.stoneFull.toString()}`,
        `objects ${performanceSnapshot.displayObjects.toString()} sprites player/form/sword ${performanceSnapshot.playerSprites.toString()}/${performanceSnapshot.formationSprites.toString()}/${performanceSnapshot.swordSprites.toString()}`,
        `effects pickup/attack ${performanceSnapshot.pickupEffects.toString()}/${performanceSnapshot.attackEffects.toString()}`,
        `messages ${performanceSnapshot.receivedMessageCount.toString()} rate ${performanceSnapshot.messagesPerSecond.toFixed(1)}/s heap ${formatHeapUsed(performanceSnapshot.heapUsedMb)}`,
        `dbg(hold \`) 1F:${this.debugFormationLayerLimit.toString()} 2B:${formatDebugToggle(this.debugSimpleBackground)} 3S:${formatDebugToggle(this.debugSimpleStones)} 4M:${formatDebugToggle(this.debugMinimapVisible)} 5P:${formatDebugToggle(this.debugPlayerSpritesVisible)}`,
      ].join("\n"),
    );
  }

  private handleControlShortcuts(): void {
    this.handlePerformanceDebugShortcuts();

    const self = this.viewState?.lastSnapshot?.self;
    if (!self?.isAlive) {
      if (this.mouseLockActive && !this.mouseLockRestorePending) {
        this.exitMouseLock(true);
      }
      return;
    }
    this.mouseLockRestorePending = false;

    if (
      this.mouseLockKey &&
      Phaser.Input.Keyboard.JustDown(this.mouseLockKey)
    ) {
      if (this.mouseLockActive) {
        this.exitMouseLock();
      } else {
        this.enterMouseLock(self.facing);
      }
    }

    if (this.escapeKey && Phaser.Input.Keyboard.JustDown(this.escapeKey)) {
      this.exitMouseLock();
    }
  }

  private handlePerformanceDebugShortcuts(): void {
    if (!ENABLE_PERFORMANCE_DEBUG) {
      return;
    }
    if (isTextInputActive()) {
      return;
    }
    // Debug toggles only fire while the backtick modifier is held, preventing
    // accidental activation (e.g. hiding all formation layers) during play.
    if (!this.debugModifierKey?.isDown) {
      return;
    }
    const [formationKey, backgroundKey, stoneKey, minimapKey, playerKey] =
      this.debugToggleKeys;
    if (formationKey && Phaser.Input.Keyboard.JustDown(formationKey)) {
      this.debugFormationLayerLimit =
        (this.debugFormationLayerLimit + 1) %
        (MAX_TEXTURED_FORMATION_LAYERS + 1);
      this.store.recordLog(
        formatDebugToggleLog(
          this.getLocale(),
          "formationLayers",
          this.debugFormationLayerLimit,
        ),
        this.getLocale(),
      );
    }
    if (backgroundKey && Phaser.Input.Keyboard.JustDown(backgroundKey)) {
      this.debugSimpleBackground = !this.debugSimpleBackground;
      this.store.recordLog(
        formatDebugToggleLog(
          this.getLocale(),
          "background",
          this.debugSimpleBackground ? "simplified" : "full",
        ),
        this.getLocale(),
      );
    }
    if (stoneKey && Phaser.Input.Keyboard.JustDown(stoneKey)) {
      this.debugSimpleStones = !this.debugSimpleStones;
      this.store.recordLog(
        formatDebugToggleLog(
          this.getLocale(),
          "stones",
          this.debugSimpleStones ? "simplified" : "full",
        ),
        this.getLocale(),
      );
    }
    if (minimapKey && Phaser.Input.Keyboard.JustDown(minimapKey)) {
      this.debugMinimapVisible = !this.debugMinimapVisible;
      this.store.recordLog(
        formatDebugToggleLog(
          this.getLocale(),
          "minimap",
          this.debugMinimapVisible ? "shown" : "hidden",
        ),
        this.getLocale(),
      );
    }
    if (playerKey && Phaser.Input.Keyboard.JustDown(playerKey)) {
      this.debugPlayerSpritesVisible = !this.debugPlayerSpritesVisible;
      this.store.recordLog(
        formatDebugToggleLog(
          this.getLocale(),
          "playerSprites",
          this.debugPlayerSpritesVisible ? "shown" : "hidden",
        ),
        this.getLocale(),
      );
    }
  }

  private enterMouseLock(facing: number, restorePending = false): void {
    this.controlMode = "mouse";
    this.mouseLockPreferred = true;
    this.mouseLockRestorePending = restorePending;
    this.lockedCursorPosition = this.createLockedCursorPosition(facing);
    this.input.mouse?.requestPointerLock();
    this.syncMouseLockState();
    this.notifyMouseLockStatusChanged();
  }

  private exitMouseLock(keepPreference = false): void {
    if (document.pointerLockElement === this.game.canvas) {
      document.exitPointerLock();
    }
    if (!keepPreference) {
      this.mouseLockPreferred = false;
      this.mouseLockRestorePending = false;
    }
    this.mouseLockActive = false;
    this.lockedCursorPosition = null;
    this.setCanvasCursor("auto");
    this.lockedCursorLayer?.clear();
    this.notifyMouseLockStatusChanged();
  }

  private syncMouseLockState(): void {
    const wasActive = this.mouseLockActive;
    this.mouseLockActive = document.pointerLockElement === this.game.canvas;
    if (this.mouseLockActive) {
      const self = this.viewState?.lastSnapshot?.self;
      this.lockedCursorPosition ??= this.createLockedCursorPosition(
        self?.facing ?? this.lastSentAngle,
      );
      this.setCanvasCursor("none");
    } else {
      this.lockedCursorPosition = null;
      this.setCanvasCursor("auto");
      this.lockedCursorLayer?.clear();
    }
    if (wasActive !== this.mouseLockActive) {
      this.notifyMouseLockStatusChanged();
    }
  }

  private notifyMouseLockStatusChanged(): void {
    for (const listener of this.mouseLockStatusListeners) {
      listener(this.mouseLockActive, this.mouseLockPreferred);
    }
  }

  private createLockedCursorPosition(facing: number): { x: number; y: number } {
    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    const pointer = this.input.activePointer;
    const pointerDistance = Math.hypot(
      pointer.x - centerX,
      pointer.y - centerY,
    );
    if (pointerDistance >= LOCKED_CURSOR_MIN_DISTANCE) {
      return this.clampScreenPointToLockedCursor({
        x: pointer.x,
        y: pointer.y,
      });
    }
    return {
      x: centerX + Math.cos(facing) * LOCKED_CURSOR_RADIUS * 0.55,
      y: centerY + Math.sin(facing) * LOCKED_CURSOR_RADIUS * 0.55,
    };
  }

  private moveLockedCursor(deltaX: number, deltaY: number): void {
    const position =
      this.lockedCursorPosition ??
      this.createLockedCursorPosition(
        this.viewState?.lastSnapshot?.self?.facing ?? this.lastSentAngle,
      );
    this.lockedCursorPosition = this.clampScreenPointToLockedCursor({
      x: position.x + deltaX,
      y: position.y + deltaY,
    });
  }

  private clampLockedCursor(): void {
    if (!this.lockedCursorPosition) {
      return;
    }
    this.lockedCursorPosition = this.clampScreenPointToLockedCursor(
      this.lockedCursorPosition,
    );
  }

  private clampScreenPointToLockedCursor(point: { x: number; y: number }): {
    x: number;
    y: number;
  } {
    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    const deltaX = point.x - centerX;
    const deltaY = point.y - centerY;
    const distance = Math.hypot(deltaX, deltaY);
    if (distance <= LOCKED_CURSOR_RADIUS) {
      return point;
    }
    const scale = LOCKED_CURSOR_RADIUS / Math.max(distance, 1);
    return {
      x: centerX + deltaX * scale,
      y: centerY + deltaY * scale,
    };
  }

  private setCanvasCursor(cursor: string): void {
    this.game.canvas.style.cursor = cursor;
  }

  private drawBackdrop(): void {
    if (!this.background) {
      return;
    }

    const width = this.scale.width;
    const height = this.scale.height;
    this.background.clear();
    this.background.fillGradientStyle(
      0x05070f,
      0x0b1020,
      0x1b1120,
      0x070a11,
      1,
    );
    this.background.fillRect(0, 0, width, height);

    this.hintText?.setPosition(24, height - 34);
  }

  private drawLockedCursor(): void {
    if (!this.lockedCursorLayer) {
      return;
    }
    this.lockedCursorLayer.clear();
    if (!this.mouseLockActive || !this.lockedCursorPosition) {
      return;
    }

    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    const cursor = this.lockedCursorPosition;
    const angle = Math.atan2(cursor.y - centerY, cursor.x - centerX);
    const pulse = (Math.sin(this.time.now / 150) + 1) * 0.5;

    this.lockedCursorLayer.lineStyle(2, 0xfff1a8, 0.35);
    this.lockedCursorLayer.lineBetween(centerX, centerY, cursor.x, cursor.y);
    this.lockedCursorLayer.fillStyle(0xfff1a8, 0.18);
    this.lockedCursorLayer.fillCircle(centerX, centerY, 4);

    const size = 10 + pulse * 2;
    const tip = {
      x: cursor.x + Math.cos(angle) * size,
      y: cursor.y + Math.sin(angle) * size,
    };
    const left = {
      x: cursor.x + Math.cos(angle + 2.35) * size * 0.72,
      y: cursor.y + Math.sin(angle + 2.35) * size * 0.72,
    };
    const right = {
      x: cursor.x + Math.cos(angle - 2.35) * size * 0.72,
      y: cursor.y + Math.sin(angle - 2.35) * size * 0.72,
    };
    this.lockedCursorLayer.fillStyle(0xfff1a8, 0.9);
    this.lockedCursorLayer.fillTriangle(
      tip.x,
      tip.y,
      left.x,
      left.y,
      right.x,
      right.y,
    );
    this.lockedCursorLayer.lineStyle(2, 0x9ce6ff, 0.65 + pulse * 0.2);
    this.lockedCursorLayer.strokeCircle(cursor.x, cursor.y, 13 + pulse * 3);
    this.lockedCursorLayer.lineStyle(1, 0xffffff, 0.6);
    this.lockedCursorLayer.lineBetween(
      cursor.x - Math.cos(angle) * 16,
      cursor.y - Math.sin(angle) * 16,
      cursor.x + Math.cos(angle) * 16,
      cursor.y + Math.sin(angle) * 16,
    );
  }

  private applyRuntimeCameraZoom(): void {
    const zoom = getRuntimeCameraZoom(readMobileRuntimeSnapshot());
    if (Math.abs(this.cameras.main.zoom - zoom) < 0.001) {
      return;
    }
    this.cameras.main.setZoom(zoom);
  }

  private isMobileRuntime(): boolean {
    return (
      this.mobileControls?.isMobileEnabled() ??
      readMobileRuntimeSnapshot().isMobile
    );
  }

  private drawWorld(time: number, delta: number): void {
    if (!this.worldLayer || !this.worldGroundLayer || !this.viewState) {
      return;
    }

    const snapshot = this.viewState.lastSnapshot;
    const self = snapshot?.self;
    const camera = this.cameras.main;
    const spectateTarget = this.findDeathSpectateTarget();
    if (spectateTarget) {
      camera.centerOn(spectateTarget.x, spectateTarget.y);
    } else if (self) {
      camera.centerOn(self.x, self.y);
    } else {
      camera.centerOn(0, 0);
    }

    this.worldLayer.clear();
    this.worldGroundLayer.clear();
    this.stoneRenderStats = createEmptyStoneRenderStats();
    this.beginSwordVisualFrame();
    if (this.debugSimpleBackground) {
      this.hideTerrainTile();
      drawSimpleWorldBoundary(
        this.worldLayer,
        this.viewState.coreBaseline.arenaRadius,
      );
    } else {
      drawSimpleWorldGround(
        this.worldGroundLayer,
        this.viewState.coreBaseline.arenaRadius,
      );
      drawSimpleWorldBoundary(
        this.worldLayer,
        this.viewState.coreBaseline.arenaRadius,
      );
      this.updateTerrainTile(this.viewState.coreBaseline.arenaRadius);
    }

    if (!snapshot) {
      this.clearMinimap();
      this.hideUnclaimedTreasure();
      this.hideStaleTexturedFormationSprites(new Set());
      this.hideStalePlayerSprites(new Set());
      this.finishSwordVisualFrame();
      return;
    }

    if (this.debugMinimapVisible) {
      this.drawMinimap(snapshot, this.viewState.coreBaseline.arenaRadius);
    } else {
      this.clearMinimap();
    }
    this.drawDropBurstEffects();

    this.worldLayer.fillStyle(0x6ae3b1, 0.95);
    for (const stone of snapshot.visibleStones) {
      this.drawSpiritStone(stone);
    }
    this.drawUnclaimedTreasure(snapshot, time);

    for (const player of snapshot.visiblePlayers) {
      this.drawPlayer(player, false);
    }

    if (self) {
      this.updateGrowthFeedback(self);
      this.drawPlayer(self, true);
      if (SHOW_STAMINA_BAR) {
        this.drawSelfStaminaBar(self, time, delta);
      } else {
        this.displayedStaminaRatio = null;
      }
    }
    this.drawPickupEffects(snapshot);
    this.drawAttackEffects(snapshot);
    this.drawWindupTargeting(snapshot);

    const visiblePlayerIds = new Set<string>();
    for (const player of snapshot.visiblePlayers) {
      this.drawPlayerLabel(player, false);
      visiblePlayerIds.add(player.id);
    }
    if (self) {
      this.drawPlayerLabel(self, true);
      visiblePlayerIds.add(self.id);
      this.updateSoftBumpAudio(snapshot);
    }
    this.hideStalePlayerLabels(visiblePlayerIds);
    this.hideStaleTexturedFormationSprites(visiblePlayerIds);
    this.hideStalePlayerSprites(visiblePlayerIds);
    this.finishSwordVisualFrame();
  }

  private findDeathSpectateTarget(): ServerSnapshotMessage["self"] | null {
    const spectate = this.viewState?.deathSpectate;
    const snapshot = this.viewState?.lastSnapshot;
    if (!spectate || !snapshot) {
      return null;
    }
    const target = snapshot.visiblePlayers.find(
      (player) => player.id === spectate.targetId,
    );
    return target?.isAlive ? target : null;
  }

  private getRenderFocusPoint(): Pick<
    ServerSnapshotMessage["self"],
    "x" | "y"
  > | null {
    return (
      this.findDeathSpectateTarget() ??
      this.viewState?.lastSnapshot?.self ??
      null
    );
  }

  /**
   * Local-only ground pattern selection. Stored as a field and applied on the
   * next frame by drawWorld, so it can switch live without a reconnect.
   */
  setTerrainPattern(pattern: TerrainPatternId): void {
    this.terrainPattern = normalizeTerrainPattern(pattern);
  }

  /** Local-only: which skin to render for the self player. */
  setSelfSkin(skinId: SkinId): void {
    this.selfSkinId = skinId;
  }

  private hideTerrainTile(): void {
    this.terrainTileSprite?.setVisible(false);
  }

  private updateTerrainTile(arenaRadius: number): void {
    const tile = this.terrainTileSprite;
    const mask = this.terrainMaskGraphics;
    if (!tile || !mask) {
      return;
    }
    const key = ensureTerrainPatternTexture(this, this.terrainPattern);
    if (!key) {
      tile.setVisible(false);
      return;
    }
    if (tile.texture.key !== key) {
      tile.setTexture(key);
    }
    const size = arenaRadius * 2;
    tile.setPosition(0, 0).setSize(size, size).setVisible(true);
    mask.clear();
    mask.fillStyle(0xffffff, 1);
    mask.fillCircle(0, 0, arenaRadius);
  }

  private updateSoftBumpAudio(snapshot: ServerSnapshotMessage): void {
    const self = snapshot.self;
    if (
      !self.isAlive ||
      this.time.now - this.lastSoftBumpAt < SOFT_BUMP_COOLDOWN_MS
    ) {
      return;
    }

    const overlapped = snapshot.visiblePlayers.some((player) => {
      if (!player.isAlive) {
        return false;
      }
      const distance = Math.hypot(player.x - self.x, player.y - self.y);
      return distance < (player.radius + self.radius) * 0.88;
    });
    if (!overlapped) {
      return;
    }

    this.lastSoftBumpAt = this.time.now;
    this.playAudioCueKind("soft_bump");
  }

  private drawUnclaimedTreasure(
    snapshot: ServerSnapshotMessage,
    time: number,
  ): void {
    const treasure = snapshot.unclaimedTreasure;
    if (!treasure) {
      this.hideUnclaimedTreasure();
      return;
    }

    const style = getTreasureUiConfig(treasure.kind);
    const pulse = (Math.sin(time / 220) + 1) * 0.5;
    const radius = 24 + pulse * 7;
    this.worldLayer?.fillStyle(style.glowColor, 0.12 + pulse * 0.08);
    this.worldLayer?.fillCircle(treasure.x, treasure.y, radius);
    this.worldLayer?.lineStyle(2.2, style.glowColor, 0.48 + pulse * 0.24);
    this.worldLayer?.strokeCircle(treasure.x, treasure.y, radius + 5);
    this.worldLayer?.lineStyle(1.2, 0xffffff, 0.52);
    this.worldLayer?.strokeCircle(treasure.x, treasure.y, 16 + pulse * 2);

    const textureKey = getTreasureTextureKey(treasure.kind);
    if (!this.treasureSprite) {
      this.treasureSprite = this.add.image(treasure.x, treasure.y, textureKey);
      this.treasureSprite.setDepth(2.75);
    } else if (this.treasureSprite.texture.key !== textureKey) {
      this.treasureSprite.setTexture(textureKey);
    }

    this.treasureSprite
      .setPosition(treasure.x, treasure.y)
      .setRotation(treasure.facing + Math.sin(time / 520) * 0.12)
      .setDisplaySize(TREASURE_WORLD_DISPLAY_SIZE, TREASURE_WORLD_DISPLAY_SIZE)
      .setAlpha(0.92)
      .setVisible(true);
  }

  private hideUnclaimedTreasure(): void {
    this.treasureSprite?.setVisible(false);
  }

  private drawPickupEffects(snapshot: ServerSnapshotMessage): void {
    if (!this.worldLayer || !this.viewState) {
      return;
    }

    const now = Date.now();
    const playersById = new Map(
      [snapshot.self, ...snapshot.visiblePlayers].map((player) => [
        player.id,
        player,
      ]),
    );
    let drawnCount = 0;
    const effectLimit = this.isMobileRuntime()
      ? MOBILE_PICKUP_EFFECT_LIMIT
      : Number.POSITIVE_INFINITY;
    for (const effect of this.viewState.pickupEffects) {
      if (drawnCount >= effectLimit) {
        break;
      }
      const target = playersById.get(effect.playerId);
      if (!target) {
        continue;
      }
      const elapsed = now - effect.createdAt;
      if (elapsed < 0 || elapsed > PICKUP_EFFECT_MS) {
        continue;
      }
      this.drawPickupEffect(effect, target, elapsed / PICKUP_EFFECT_MS);
      drawnCount += 1;
    }
    this.store.prunePickupEffects(now - PICKUP_EFFECT_MS - 100);
  }

  private drawDropBurstEffects(): void {
    if (!this.worldLayer || !this.viewState) {
      return;
    }

    const now = Date.now();
    let drawnCount = 0;
    const effectLimit = this.isMobileRuntime()
      ? MOBILE_DROP_BURST_EFFECT_LIMIT
      : Number.POSITIVE_INFINITY;
    for (const effect of this.viewState.dropBurstEffects) {
      if (drawnCount >= effectLimit) {
        break;
      }
      const elapsed = now - effect.createdAt;
      if (elapsed < 0 || elapsed > DROP_BURST_EFFECT_MS) {
        continue;
      }
      this.drawDropBurstEffect(effect, elapsed / DROP_BURST_EFFECT_MS);
      drawnCount += 1;
    }
    this.store.pruneDropBurstEffects(now - DROP_BURST_EFFECT_MS - 100);
  }

  private drawDropBurstEffect(effect: DropBurstEffect, progress: number): void {
    if (!this.worldLayer) {
      return;
    }

    const style = getSpiritStoneVisualStyle(effect.value);
    const power = Math.min(1, Math.log10(Math.max(10, effect.value)) / 6);
    const easeOut = 1 - (1 - progress) ** 3;
    const fade = Math.max(0, 1 - progress);
    const coreRadius = 34 + power * 46;
    const shockRadius = coreRadius + easeOut * (90 + power * 95);
    const ringAlpha = fade * (0.34 + power * 0.28);
    const fillAlpha = fade * (0.05 + power * 0.06);

    this.worldLayer.fillStyle(style.ringColor, fillAlpha);
    this.worldLayer.fillCircle(effect.x, effect.y, shockRadius);
    this.worldLayer.lineStyle(2.2 + power * 2.2, style.ringColor, ringAlpha);
    this.worldLayer.strokeCircle(effect.x, effect.y, shockRadius);
    this.worldLayer.lineStyle(1.4, 0x192235, fade * 0.32);
    this.worldLayer.strokeCircle(effect.x, effect.y, coreRadius + easeOut * 18);

    const rayCount = 6 + Math.round(power * 6);
    this.worldLayer.lineStyle(1.4 + power, style.highlightColor, fade * 0.35);
    for (let index = 0; index < rayCount; index += 1) {
      const angle =
        (Math.PI * 2 * index) / rayCount +
        Math.sin(effect.x * 0.013 + effect.y * 0.019) * 0.8;
      const inner = coreRadius * (0.72 + progress * 0.18);
      const outer = shockRadius * (0.82 + power * 0.08);
      this.worldLayer.beginPath();
      this.worldLayer.moveTo(
        effect.x + Math.cos(angle) * inner,
        effect.y + Math.sin(angle) * inner,
      );
      this.worldLayer.lineTo(
        effect.x + Math.cos(angle) * outer,
        effect.y + Math.sin(angle) * outer,
      );
      this.worldLayer.strokePath();
    }
  }

  private drawPickupEffect(
    effect: PickupEffect,
    target: ServerSnapshotMessage["self"],
    progress: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const eased = 1 - (1 - progress) ** 3;
    const dx = target.x - effect.x;
    const dy = target.y - effect.y;
    const distance = Math.hypot(dx, dy);
    const curveSign =
      Math.sin(effect.x * 0.017 + effect.y * 0.031 + effect.value) >= 0
        ? 1
        : -1;
    const curveOffset = Math.min(52, Math.max(10, distance * 0.18)) * curveSign;
    const normalX = distance > 0 ? -dy / distance : 0;
    const normalY = distance > 0 ? dx / distance : -1;
    const controlX = (effect.x + target.x) * 0.5 + normalX * curveOffset;
    const controlY = (effect.y + target.y) * 0.5 + normalY * curveOffset;
    const oneMinusEased = 1 - eased;
    const x =
      oneMinusEased * oneMinusEased * effect.x +
      2 * oneMinusEased * eased * controlX +
      eased * eased * target.x;
    const y =
      oneMinusEased * oneMinusEased * effect.y +
      2 * oneMinusEased * eased * controlY +
      eased * eased * target.y;
    const style = getSpiritStoneVisualStyle(effect.value);
    const highValueBoost = Math.min(
      1,
      Math.log10(Math.max(1, effect.value)) / 5,
    );
    const alpha =
      Math.max(0, 1 - progress * (1.12 - highValueBoost * 0.18)) *
      Math.min(1, style.alpha + 0.12);
    const radius = Math.max(
      1.5,
      style.radius * (1 - progress * (0.52 + highValueBoost * 0.12)),
    );
    const rotation = Math.atan2(dy, dx) + Math.PI / 2;

    this.drawSpiritStoneShape(x, y, radius, rotation, {
      ...style,
      alpha,
      coreAlpha: style.coreAlpha * alpha,
      ringAlpha: style.ringAlpha * alpha * 0.7,
      highlightAlpha: style.highlightAlpha * alpha,
      auraAlpha: 0,
      sparkleAlpha: 0,
      sparkleCount: 0,
    });

    if (progress > 0.62) {
      const absorbProgress = (progress - 0.62) / 0.38;
      const absorbAlpha = (1 - absorbProgress) * (0.22 + highValueBoost * 0.28);
      const absorbRadius =
        target.radius * (0.75 + absorbProgress * 0.42) + highValueBoost * 10;
      this.worldLayer.lineStyle(
        1.2 + highValueBoost * 1.2,
        style.ringColor,
        absorbAlpha,
      );
      this.worldLayer.strokeCircle(target.x, target.y, absorbRadius);
      this.worldLayer.fillStyle(style.ringColor, absorbAlpha * 0.1);
      this.worldLayer.fillCircle(target.x, target.y, absorbRadius * 0.7);
    }
  }

  private drawAttackEffects(snapshot: ServerSnapshotMessage): void {
    if (!this.worldLayer || !this.viewState) {
      return;
    }

    const now = Date.now();
    const playersById = new Map(
      [snapshot.self, ...snapshot.visiblePlayers].map((player) => [
        player.id,
        player,
      ]),
    );
    let drawnCount = 0;
    const effectLimit = this.isMobileRuntime()
      ? MOBILE_ATTACK_EFFECT_LIMIT
      : Number.POSITIVE_INFINITY;
    for (const effect of this.viewState.attackEffects) {
      if (drawnCount >= effectLimit) {
        break;
      }
      const attacker = playersById.get(effect.attackerId);
      if (!attacker) {
        continue;
      }
      const elapsed = now - effect.createdAt;
      const duration = getAttackEffectVisualDuration(effect, attacker.level);
      if (elapsed < 0 || elapsed > duration) {
        continue;
      }
      const target = effect.targetId ? playersById.get(effect.targetId) : null;
      if (effect.result === "started") {
        this.drawSwordWindup(effect, attacker, elapsed / duration);
      } else {
        this.drawSwordSlash(
          effect,
          attacker,
          target ?? null,
          elapsed / duration,
          elapsed,
          duration,
        );
      }
      drawnCount += 1;
    }
    this.store.pruneAttackEffects(now - ATTACK_EFFECT_MAX_MS);
  }

  private drawWindupTargeting(snapshot: ServerSnapshotMessage): void {
    if (!this.worldLayer) {
      return;
    }

    const players = [snapshot.self, ...snapshot.visiblePlayers];
    const playersById = new Map(players.map((player) => [player.id, player]));
    for (const attacker of players) {
      if (!attacker.isAlive || attacker.attackPhase !== "windup") {
        continue;
      }

      const target = attacker.attackTargetId
        ? playersById.get(attacker.attackTargetId)
        : null;
      this.drawWindupTargetLine(attacker, target ?? null, snapshot.self.id);
    }
  }

  private drawWindupTargetLine(
    attacker: ServerSnapshotMessage["self"],
    target: ServerSnapshotMessage["self"] | null,
    selfId: string,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const isSelf = attacker.id === selfId;
    const style = getAttackFormationStyle("windup", isSelf, attacker.level);
    const pulse = 0.5 + Math.sin(this.time.now / 92) * 0.5;
    const startDistance = attacker.radius + 10;
    const targetPoint = target?.isAlive
      ? { x: target.x, y: target.y }
      : getMissDestination(
          { x: attacker.x, y: attacker.y },
          attacker.facing,
          Math.min(attacker.attackRadius, attacker.radius + 86),
        );
    const angle = Math.atan2(
      targetPoint.y - attacker.y,
      targetPoint.x - attacker.x,
    );
    const startX = attacker.x + Math.cos(angle) * startDistance;
    const startY = attacker.y + Math.sin(angle) * startDistance;
    const endDistance = target
      ? Math.max(
          startDistance + 16,
          Phaser.Math.Distance.Between(
            attacker.x,
            attacker.y,
            targetPoint.x,
            targetPoint.y,
          ) -
            target.radius -
            6,
        )
      : Math.min(attacker.attackRadius * 0.62, attacker.radius + 92);
    const endX = attacker.x + Math.cos(angle) * endDistance;
    const endY = attacker.y + Math.sin(angle) * endDistance;
    const targetIsSelf = target?.id === selfId;
    const alphaBase = targetIsSelf ? 0.74 : isSelf ? 0.34 : 0.6;

    this.worldLayer.lineStyle(6.4, style.frontArcColor, alphaBase * 0.24);
    this.worldLayer.lineBetween(startX, startY, endX, endY);
    this.worldLayer.lineStyle(
      1.8 + pulse * 1.2,
      style.innerColor,
      alphaBase + pulse * 0.22,
    );
    this.worldLayer.lineBetween(startX, startY, endX, endY);

    if (target?.isAlive) {
      this.drawWindupTargetMarker(target, style, isSelf, targetIsSelf, pulse);
    } else {
      this.drawWindupFallbackSpark(endX, endY, angle, style, alphaBase);
    }
  }

  private drawWindupTargetMarker(
    target: ServerSnapshotMessage["self"],
    style: AttackFormationStyle,
    isSelfAttack: boolean,
    isTargetSelf: boolean,
    pulse: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const radius = Math.max(
      target.radius + 22,
      Math.min(84, target.attackRadius * 0.42),
    );
    const pulseRadius = radius + pulse * 7;
    const color = isTargetSelf ? 0xff6b4a : style.frontArcColor;
    const innerColor = isTargetSelf ? 0xfff1a8 : style.innerColor;
    const alpha =
      (isTargetSelf ? 0.92 : isSelfAttack ? 0.52 : 0.78) + pulse * 0.16;

    this.worldLayer.fillStyle(color, Math.min(0.12, alpha * 0.12));
    this.worldLayer.fillCircle(target.x, target.y, pulseRadius + 6);
    this.worldLayer.lineStyle(6.2, color, alpha * 0.18);
    this.worldLayer.strokeCircle(target.x, target.y, pulseRadius + 3);
    this.worldLayer.lineStyle(3.6, color, alpha);
    this.worldLayer.strokeCircle(target.x, target.y, pulseRadius);
    this.worldLayer.lineStyle(1.65, innerColor, Math.min(1, alpha * 0.9));
    this.worldLayer.strokeCircle(target.x, target.y, Math.max(8, radius - 11));

    const bracketRadius = pulseRadius + 8;
    const bracketLength = Math.max(12, radius * 0.28) + pulse * 4;
    const rotation = this.time.now / 210;
    for (let index = 0; index < 4; index += 1) {
      const angle = rotation + index * (Math.PI / 2) + Math.PI / 4;
      const x = target.x + Math.cos(angle) * bracketRadius;
      const y = target.y + Math.sin(angle) * bracketRadius;
      const tangentX = Math.cos(angle + Math.PI / 2);
      const tangentY = Math.sin(angle + Math.PI / 2);
      this.worldLayer.lineStyle(3.2, color, Math.min(1, alpha));
      this.worldLayer.lineBetween(
        x - tangentX * bracketLength,
        y - tangentY * bracketLength,
        x + tangentX * bracketLength,
        y + tangentY * bracketLength,
      );
    }
  }

  private drawWindupFallbackSpark(
    x: number,
    y: number,
    angle: number,
    style: AttackFormationStyle,
    alpha: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const sideX = Math.cos(angle + Math.PI / 2);
    const sideY = Math.sin(angle + Math.PI / 2);
    this.worldLayer.lineStyle(2, style.innerColor, alpha * 0.72);
    this.worldLayer.lineBetween(
      x - sideX * 8,
      y - sideY * 8,
      x + sideX * 8,
      y + sideY * 8,
    );
    this.worldLayer.lineStyle(1.4, style.frontArcColor, alpha * 0.6);
    this.worldLayer.strokeCircle(x, y, 6);
  }

  private drawSwordWindup(
    effect: AttackEffect,
    attacker: ServerSnapshotMessage["self"],
    progress: number,
  ): void {
    const eased = progress ** 0.6;
    const alpha = Math.min(0.72, 0.12 + eased * 0.6);
    const origin = getAttackEffectPoint(effect, "attacker", attacker);
    if (!origin) {
      return;
    }
    const destination = getAttackEffectPoint(effect, "target", null);
    const baseAngle = destination
      ? Math.atan2(destination.y - origin.y, destination.x - origin.x)
      : (effect.facing ?? attacker.facing);
    const distance =
      attacker.radius +
      18 +
      progress * Math.min(34, attacker.attackRadius * 0.22);
    const x = origin.x + Math.cos(baseAngle) * distance;
    const y = origin.y + Math.sin(baseAngle) * distance;
    const sideX = Math.cos(baseAngle + Math.PI / 2);
    const sideY = Math.sin(baseAngle + Math.PI / 2);

    this.worldLayer?.lineStyle(2.2, 0x9ce6ff, alpha * 0.48);
    this.worldLayer?.lineBetween(
      x - sideX * 10,
      y - sideY * 10,
      x + sideX * 10,
      y + sideY * 10,
    );
    this.worldLayer?.lineStyle(1.4, 0xfff1a8, alpha * 0.58);
    this.worldLayer?.strokeCircle(x, y, 5 + eased * 8);
  }

  private drawSwordSlash(
    effect: AttackEffect,
    attacker: ServerSnapshotMessage["self"],
    target: ServerSnapshotMessage["self"] | null,
    progress: number,
    elapsedMs: number,
    durationMs: number,
  ): void {
    const swordCount = this.isMobileRuntime()
      ? Math.min(6, getSwordCountForLevel(attacker.level))
      : getSwordCountForLevel(attacker.level);
    const origin = getAttackEffectPoint(effect, "attacker", attacker);
    if (!origin) {
      return;
    }
    const destination =
      getAttackEffectPoint(effect, "target", target) ??
      getMissDestination(
        origin,
        effect.facing ?? attacker.facing,
        attacker.attackRadius,
      );
    const alpha = Math.max(0, 1 - progress * 0.8);
    const hit = effect.result === "hit";

    for (let index = 0; index < swordCount; index += 1) {
      const launchDelay = getSwordCascadeDelay(index, swordCount);
      if (elapsedMs < launchDelay) {
        continue;
      }
      const swordElapsed = elapsedMs - launchDelay;
      const launchProgress = Phaser.Math.Clamp(
        swordElapsed / SWORD_SLASH_TRAVEL_MS,
        0,
        1,
      );
      const localProgress = 1 - (1 - launchProgress) ** 3;
      const fadeEndMs = hit
        ? SWORD_SLASH_TRAVEL_MS + 92
        : SWORD_SLASH_TRAVEL_MS + SWORD_MISS_RETURN_MS;
      const swordAlpha =
        Math.min(1, alpha + 0.18) *
        Phaser.Math.Clamp(swordElapsed / 18, 0, 1) *
        Phaser.Math.Clamp((fadeEndMs - swordElapsed) / 120, 0, 1);
      const launchPoint = getSwordLaunchPoint(attacker, index);
      const initialDirection = Math.atan2(
        destination.y - launchPoint.y,
        destination.x - launchPoint.x,
      );
      const swordLength =
        getFlyingSwordDisplayWidth(21 + attacker.level * 2, "slash") * 0.5;
      const centerTargetX =
        destination.x - Math.cos(initialDirection) * swordLength;
      const centerTargetY =
        destination.y - Math.sin(initialDirection) * swordLength;
      const missReturn = hit
        ? null
        : getMissReturnSwordPose(
            attacker,
            index,
            launchPoint,
            { x: centerTargetX, y: centerTargetY },
            initialDirection,
            swordElapsed,
          );
      const x =
        missReturn?.x ??
        Phaser.Math.Linear(launchPoint.x, centerTargetX, localProgress);
      const y =
        missReturn?.y ??
        Phaser.Math.Linear(launchPoint.y, centerTargetY, localProgress);
      const direction =
        missReturn?.angle ?? Math.atan2(destination.y - y, destination.x - x);
      const trailLength = 26 + launchProgress * 18;
      this.drawSwordTrail(
        x - Math.cos(direction) * trailLength,
        y - Math.sin(direction) * trailLength,
        x,
        y,
        swordAlpha * 0.58,
      );
      this.drawFlyingSword({
        x,
        y,
        angle: direction,
        length: 21 + attacker.level * 2,
        alpha: swordAlpha,
        tone: "slash",
        bright: true,
      });
    }

    const burstStart =
      attacker.level <= LOW_TIER_ATTACK_LEVEL_MAX ? 0.46 : 0.58;
    if (hit && progress > burstStart) {
      const burstProgress = (progress - burstStart) / (1 - burstStart);
      this.drawSwordHitBurst(
        destination.x,
        destination.y,
        burstProgress,
        attacker.level,
      );
    }
  }

  private drawFlyingSword(style: FlyingSwordVisualStyle): void {
    if (!this.worldLayer) {
      return;
    }

    if (this.textures.exists(FLYING_SWORD_TEXTURE_KEY)) {
      const sprite = this.getSwordSprite();
      const tint = getFlyingSwordTint(style);
      const width = getFlyingSwordDisplayWidth(style.length, style.tone);
      const height =
        Math.max(12, style.length * 0.56) * FLYING_SWORD_DISPLAY_SCALE;
      sprite
        .setTexture(FLYING_SWORD_TEXTURE_KEY)
        .setPosition(style.x, style.y)
        .setRotation(style.angle)
        .setDisplaySize(width, height)
        .setAlpha(style.alpha)
        .setTint(tint)
        .setVisible(true);
      return;
    }

    this.drawFlyingSwordFallback(style);
  }

  private getSwordSprite(): Phaser.GameObjects.Image {
    const existing = this.swordSprites[this.swordSpriteCursor];
    this.swordSpriteCursor += 1;
    if (existing) {
      return existing;
    }

    const sprite = this.add
      .image(0, 0, FLYING_SWORD_TEXTURE_KEY)
      .setDepth(-8)
      .setOrigin(0.5);
    this.swordSprites.push(sprite);
    return sprite;
  }

  private beginSwordVisualFrame(): void {
    this.swordSpriteCursor = 0;
  }

  private finishSwordVisualFrame(): void {
    for (
      let index = this.swordSpriteCursor;
      index < this.swordSprites.length;
      index += 1
    ) {
      this.swordSprites[index]?.setVisible(false);
    }
  }

  private drawFlyingSwordFallback(style: FlyingSwordVisualStyle): void {
    if (!this.worldLayer) {
      return;
    }

    const { x, y, angle, length, alpha, bright } = style;
    const forwardX = Math.cos(angle);
    const forwardY = Math.sin(angle);
    const sideX = Math.cos(angle + Math.PI / 2);
    const sideY = Math.sin(angle + Math.PI / 2);
    const half = length / 2;
    const width = Math.max(3, length * 0.16);
    const color = bright ? 0xfff1a8 : 0x9ce6ff;
    const points = [
      new Phaser.Math.Vector2(x + forwardX * half, y + forwardY * half),
      new Phaser.Math.Vector2(x + sideX * width, y + sideY * width),
      new Phaser.Math.Vector2(
        x - forwardX * half * 0.7,
        y - forwardY * half * 0.7,
      ),
      new Phaser.Math.Vector2(x - sideX * width, y - sideY * width),
    ];
    this.worldLayer.fillStyle(color, alpha * 0.72);
    this.worldLayer.fillPoints(points, true, true);
    this.worldLayer.lineStyle(1.2, 0xffffff, alpha * 0.85);
    this.worldLayer.strokePoints(points, true, true);
    this.worldLayer.lineStyle(1.4, color, alpha * 0.7);
    this.worldLayer.lineBetween(
      x - sideX * width * 1.8,
      y - sideY * width * 1.8,
      x + sideX * width * 1.8,
      y + sideY * width * 1.8,
    );
  }

  private drawSwordTrail(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    alpha: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    this.worldLayer.lineStyle(3, 0xfff1a8, alpha);
    this.worldLayer.lineBetween(fromX, fromY, toX, toY);
    this.worldLayer.lineStyle(1, 0xffffff, alpha * 0.65);
    this.worldLayer.lineBetween(fromX, fromY, toX, toY);
  }

  private drawSwordHitBurst(
    x: number,
    y: number,
    progress: number,
    attackerLevel: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const isLowTier = attackerLevel <= LOW_TIER_ATTACK_LEVEL_MAX;
    const alpha = Math.max(0, 1 - progress);
    const radius = (isLowTier ? 12 : 8) + progress * (isLowTier ? 26 : 20);
    this.worldLayer.fillStyle(0xfff1a8, alpha * (isLowTier ? 0.12 : 0.08));
    this.worldLayer.fillCircle(x, y, radius + 4);
    this.worldLayer.lineStyle(isLowTier ? 3.4 : 2.4, 0xfff1a8, alpha * 0.9);
    this.worldLayer.strokeCircle(x, y, radius);
    this.worldLayer.lineStyle(1.7, 0xff6b4a, alpha * 0.7);
    this.worldLayer.strokeCircle(x, y, Math.max(5, radius * 0.56));
    for (let index = 0; index < 6; index += 1) {
      const angle = (index / 6) * Math.PI * 2;
      this.worldLayer.lineStyle(isLowTier ? 2 : 1.5, 0xffffff, alpha * 0.76);
      this.worldLayer.lineBetween(
        x + Math.cos(angle) * radius * 0.35,
        y + Math.sin(angle) * radius * 0.35,
        x + Math.cos(angle) * radius,
        y + Math.sin(angle) * radius,
      );
    }
  }

  private drawMinimap(
    snapshot: ServerSnapshotMessage,
    arenaRadius: number,
  ): void {
    if (!this.minimapLayer) {
      return;
    }

    const mobileSnapshot = readMobileRuntimeSnapshot();
    const minimapPadding = mobileSnapshot.isMobile
      ? MOBILE_MINIMAP_PADDING
      : MINIMAP_PADDING;
    const requestedMinimapSize = mobileSnapshot.isMobile
      ? MOBILE_MINIMAP_SIZE
      : MINIMAP_SIZE;
    const minimumMinimapSize = mobileSnapshot.isMobile
      ? MOBILE_MINIMAP_MIN_SIZE
      : MINIMAP_MIN_SIZE;
    const viewportWidth = Math.max(1, this.scale.width);
    const viewportHeight = Math.max(1, this.scale.height);
    const requestedBottomReserve = mobileSnapshot.isMobile
      ? 0
      : MINIMAP_BOTTOM_RESERVED;
    const availableHeightWithReserve =
      viewportHeight - minimapPadding * 2 - requestedBottomReserve;
    const availableHeight =
      availableHeightWithReserve >= minimumMinimapSize
        ? availableHeightWithReserve
        : viewportHeight - minimapPadding * 2;
    const minimapSize = Math.round(
      Math.max(
        1,
        Math.min(
          requestedMinimapSize,
          viewportWidth - minimapPadding * 2,
          availableHeight,
        ),
      ),
    );
    const minimapRadiusRatio = mobileSnapshot.isMobile
      ? 0.42
      : MINIMAP_RADIUS / MINIMAP_SIZE;
    const minimapRadius = minimapSize * minimapRadiusRatio;
    const maxPanelX = Math.max(0, viewportWidth - minimapSize);
    const panelX = mobileSnapshot.isMobile
      ? Math.max(0, viewportWidth - minimapSize - minimapPadding)
      : Math.min(minimapPadding, maxPanelX);
    const maxPanelY = Math.max(0, viewportHeight - minimapSize);
    const canKeepBottomReserve =
      !mobileSnapshot.isMobile &&
      viewportHeight - minimapSize - minimapPadding >=
        minimapPadding + MINIMAP_BOTTOM_RESERVED;
    const panelY = mobileSnapshot.isMobile
      ? Math.min(
          maxPanelY,
          mobileSnapshot.orientation === "landscape"
            ? minimapPadding + MOBILE_LANDSCAPE_MINIMAP_TOP_RESERVED
            : minimapPadding,
        )
      : Math.min(
          maxPanelY,
          Math.max(
            0,
            viewportHeight -
              minimapSize -
              minimapPadding -
              (canKeepBottomReserve ? MINIMAP_BOTTOM_RESERVED : 0),
          ),
        );
    const centerX = panelX + minimapSize / 2;
    const centerY = panelY + minimapSize / 2;
    const layout: MinimapLayout = {
      centerX,
      centerY,
      radius: minimapRadius,
      arenaRadius,
    };

    this.minimapLayer.clear();
    this.minimapLayer.fillStyle(0x07101d, 0.74);
    this.minimapLayer.fillRoundedRect(
      panelX,
      panelY,
      minimapSize,
      minimapSize,
      18,
    );
    this.minimapLayer.lineStyle(1.5, PANEL_STROKE, 0.62);
    this.minimapLayer.strokeRoundedRect(
      panelX,
      panelY,
      minimapSize,
      minimapSize,
      18,
    );

    this.minimapLayer.fillStyle(0x09101c, 0.88);
    this.minimapLayer.fillCircle(centerX, centerY, minimapRadius);
    this.minimapLayer.lineStyle(1.5, 0x8fb7d8, 0.36);
    this.minimapLayer.strokeCircle(centerX, centerY, minimapRadius);
    this.minimapLayer.lineStyle(1, 0xb99146, 0.16);
    this.minimapLayer.lineBetween(
      centerX - minimapRadius,
      centerY,
      centerX + minimapRadius,
      centerY,
    );
    this.minimapLayer.lineBetween(
      centerX,
      centerY - minimapRadius,
      centerX,
      centerY + minimapRadius,
    );

    if (snapshot.resourceHeatmap && snapshot.resourceHeatmap.length > 0) {
      for (const bucket of snapshot.resourceHeatmap) {
        this.drawMinimapHeatmapBucket(bucket, layout);
      }
    } else {
      for (const stone of snapshot.visibleStones) {
        this.drawMinimapStone(stone, layout);
      }
    }
    this.drawMinimapTreasure(snapshot.unclaimedTreasure ?? null, layout);
    for (const marker of snapshot.leaderboardMarkers ?? []) {
      if (marker.id !== snapshot.self.id) {
        this.drawMinimapLeaderboardMarker(marker, layout);
      }
    }
    this.drawMinimapSelf(snapshot.self, layout);
  }

  private drawMinimapHeatmapBucket(
    bucket: NonNullable<ServerSnapshotMessage["resourceHeatmap"]>[number],
    layout: MinimapLayout,
  ): void {
    if (!this.minimapLayer) {
      return;
    }

    const point = worldToMinimapPoint(bucket, layout);
    const style = getMinimapHeatmapStyle(bucket.valueSum, bucket.maxValue);
    this.minimapLayer.fillStyle(style.color, style.alpha);
    this.minimapLayer.fillCircle(point.x, point.y, style.radius);
    if (style.ringAlpha > 0) {
      this.minimapLayer.lineStyle(1, style.ringColor, style.ringAlpha);
      this.minimapLayer.strokeCircle(point.x, point.y, style.radius + 3);
    }
  }

  private drawMinimapStone(
    stone: ServerSnapshotMessage["visibleStones"][number],
    layout: MinimapLayout,
  ): void {
    if (!this.minimapLayer) {
      return;
    }

    const point = worldToMinimapPoint(stone, layout);
    const style = getMinimapStoneStyle(stone.value);
    this.minimapLayer.fillStyle(style.color, style.alpha);
    this.minimapLayer.fillCircle(point.x, point.y, style.radius);
    if (style.ringAlpha > 0) {
      this.minimapLayer.lineStyle(1, style.ringColor, style.ringAlpha);
      this.minimapLayer.strokeCircle(
        point.x,
        point.y,
        style.radius + style.ringRadiusOffset,
      );
    }
  }

  private drawMinimapTreasure(
    treasure: ServerSnapshotMessage["unclaimedTreasure"] | null,
    layout: MinimapLayout,
  ): void {
    if (!this.minimapLayer || !treasure) {
      this.minimapTreasureSprite?.setVisible(false);
      return;
    }

    const point = worldToMinimapPoint(treasure, layout);
    const style = getTreasureUiConfig(treasure.kind);
    const pulse = (Math.sin(this.time.now / 260) + 1) * 0.5;
    this.minimapLayer.lineStyle(2, style.glowColor, 0.5 + pulse * 0.28);
    this.minimapLayer.strokeCircle(
      point.x,
      point.y,
      TREASURE_MINIMAP_RADIUS + 3 + pulse * 2,
    );
    const textureKey = getTreasureTextureKey(treasure.kind);
    if (!this.minimapTreasureSprite) {
      this.minimapTreasureSprite = this.add
        .image(point.x, point.y, textureKey)
        .setDepth(20)
        .setScrollFactor(0);
    } else if (this.minimapTreasureSprite.texture.key !== textureKey) {
      this.minimapTreasureSprite.setTexture(textureKey);
    }
    this.minimapTreasureSprite
      .setPosition(point.x, point.y)
      .setDisplaySize(22, 22)
      .setAlpha(0.96)
      .setVisible(true);
  }

  private drawMinimapLeaderboardMarker(
    marker: NonNullable<ServerSnapshotMessage["leaderboardMarkers"]>[number],
    layout: MinimapLayout,
  ): void {
    if (!this.minimapLayer) {
      return;
    }

    const point = worldToMinimapPoint(marker, layout);
    const style = getMinimapLeaderboardMarkerStyle(marker.rank);
    const pulse =
      marker.rank === 1 ? (Math.sin(this.time.now / 260) + 1) * 0.5 : 0;
    this.minimapLayer.lineStyle(
      2,
      style.ringColor,
      style.ringAlpha + pulse * 0.18,
    );
    this.minimapLayer.strokeCircle(
      point.x,
      point.y,
      style.ringRadius + pulse * 3,
    );
    this.minimapLayer.fillStyle(style.color, style.alpha);
    this.minimapLayer.fillCircle(point.x, point.y, style.radius);
  }

  private drawMinimapSelf(
    self: ServerSnapshotMessage["self"],
    layout: MinimapLayout,
  ): void {
    if (!this.minimapLayer) {
      return;
    }

    const point = worldToMinimapPoint(self, layout);
    const tip = {
      x: point.x + Math.cos(self.facing) * 6,
      y: point.y + Math.sin(self.facing) * 6,
    };
    const left = {
      x: point.x + Math.cos(self.facing + 2.45) * 5,
      y: point.y + Math.sin(self.facing + 2.45) * 5,
    };
    const right = {
      x: point.x + Math.cos(self.facing - 2.45) * 5,
      y: point.y + Math.sin(self.facing - 2.45) * 5,
    };

    this.minimapLayer.fillStyle(self.isAlive ? 0xf7e4b5 : 0xff8f8f, 0.96);
    this.minimapLayer.fillTriangle(
      tip.x,
      tip.y,
      left.x,
      left.y,
      right.x,
      right.y,
    );
    this.minimapLayer.lineStyle(2, 0xfff1a8, 0.72);
    this.minimapLayer.strokeCircle(point.x, point.y, 5.8);
  }

  private clearMinimap(): void {
    this.minimapLayer?.clear();
    this.minimapTreasureSprite?.setVisible(false);
  }

  private drawPlayer(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const fillColor = isSelf
      ? player.isAlive
        ? 0xf7e4b5
        : 0xff8f8f
      : player.isAlive
        ? 0x5cc8ff
        : 0x845b5b;

    if (player.turnBurstActive) {
      this.drawTurnBurstTrace(player, isSelf);
    }
    this.drawAttackArc(player, isSelf);
    if (
      !this.debugPlayerSpritesVisible ||
      !this.drawCultivatorSprite(player, isSelf)
    ) {
      this.hidePlayerSprite(player.id);
      this.drawCultivatorGlyph(player, isSelf);
    }
    this.worldLayer.fillStyle(fillColor, player.isAlive ? 0.8 : 0.45);
    this.worldLayer.fillCircle(
      player.x,
      player.y,
      Math.max(3, player.radius * 0.28),
    );
  }

  private drawTurnBurstTrace(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): void {
    if (!this.worldLayer || !player.isAlive) {
      return;
    }

    const alpha = isSelf ? 0.34 : 0.22;
    const color = isSelf ? 0xd7fff4 : 0x9fe8ff;
    const backAngle = player.facing + Math.PI;
    for (let index = 0; index < 3; index += 1) {
      const spread = (index - 1) * 0.36;
      const length = 32 + index * 10;
      const startDistance = player.radius * 0.55;
      const start = {
        x: player.x + Math.cos(backAngle + spread) * startDistance,
        y: player.y + Math.sin(backAngle + spread) * startDistance,
      };
      const end = {
        x: player.x + Math.cos(backAngle + spread) * length,
        y: player.y + Math.sin(backAngle + spread) * length,
      };
      this.worldLayer.lineStyle(2.5 - index * 0.35, color, alpha);
      this.worldLayer.lineBetween(start.x, start.y, end.x, end.y);
    }
  }

  private drawSelfStaminaBar(
    player: ServerSnapshotMessage["self"],
    time: number,
    delta: number,
  ): void {
    if (!this.worldLayer || !player.isAlive) {
      this.displayedStaminaRatio = null;
      return;
    }

    const targetRatio = getStaminaRatio(player.stamina, player.maxStamina);
    this.displayedStaminaRatio = smoothStaminaRatio(
      this.displayedStaminaRatio,
      targetRatio,
      delta,
    );

    const boostHeld = this.boostKey?.isDown ?? false;
    if (boostHeld && !player.boostActive && targetRatio < STAMINA_LOW_RATIO) {
      this.staminaFeedbackUntil = Math.max(
        this.staminaFeedbackUntil,
        time + STAMINA_FEEDBACK_MS,
      );
    }

    const feedbackActive = time < this.staminaFeedbackUntil;
    const style = getStaminaBarStyle(
      this.displayedStaminaRatio * player.maxStamina,
      player.maxStamina,
      feedbackActive,
    );
    const pulse = feedbackActive ? 0.5 + Math.sin(time * 0.06) * 0.5 : 0;
    const shake = feedbackActive ? Math.sin(time * 0.12) * 2.4 : 0;
    const x = player.x - STAMINA_BAR_WIDTH / 2 + shake;
    const y = player.y + player.radius + STAMINA_BAR_OFFSET;
    const fillWidth = Math.max(0, STAMINA_BAR_WIDTH * style.ratio);

    this.worldLayer.fillStyle(style.trackColor, style.trackAlpha);
    this.worldLayer.fillRoundedRect(
      x,
      y,
      STAMINA_BAR_WIDTH,
      STAMINA_BAR_HEIGHT,
      STAMINA_BAR_HEIGHT / 2,
    );
    if (fillWidth > 0) {
      this.worldLayer.fillStyle(style.glowColor, style.glowAlpha + pulse * 0.1);
      this.worldLayer.fillRoundedRect(
        x - 2,
        y - 2,
        fillWidth + 4,
        STAMINA_BAR_HEIGHT + 4,
        STAMINA_BAR_HEIGHT,
      );
      this.worldLayer.fillStyle(style.fillColor, style.fillAlpha);
      this.worldLayer.fillRoundedRect(
        x,
        y,
        fillWidth,
        STAMINA_BAR_HEIGHT,
        STAMINA_BAR_HEIGHT / 2,
      );
    }
  }

  private drawCultivatorSprite(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): boolean {
    // Self uses the locally chosen skin (authoritative even before the first
    // snapshot echoes it back); others use the skin from their snapshot, with a
    // deterministic hash fallback when absent / unknown (SKIN-3).
    const variant = resolveSkinDef(
      isSelf ? this.selfSkinId : (player.skinId ?? null),
      hashString(player.id),
    );
    if (!this.textures.exists(variant.sheetKey)) {
      return false;
    }

    const sprite = this.getPlayerSprite(player.id, variant.sheetKey);
    const animationKey = player.boostActive
      ? variant.boostAnimationKey
      : variant.moveAnimationKey;
    const size =
      CULTIVATOR_SPRITE_BASE_SIZE +
      getCultivatorVisualGrowth(player.level) *
        CULTIVATOR_SPRITE_LEVEL_SIZE_STEP;
    if (sprite.anims.currentAnim?.key !== animationKey) {
      sprite.play(animationKey, true);
    }
    sprite
      .setPosition(player.x, player.y)
      .setRotation(player.facing + Math.PI / 2)
      .setDisplaySize(size, size)
      .setAlpha(player.isAlive ? (isSelf ? 0.96 : 0.82) : 0.38)
      .setVisible(true);
    return true;
  }

  private getPlayerSprite(
    playerId: string,
    textureKey: string,
  ): Phaser.GameObjects.Sprite {
    const existing = this.playerSprites.get(playerId);
    if (existing) {
      return existing;
    }

    const sprite = this.add
      .sprite(0, 0, textureKey, 0)
      .setDepth(4)
      .setOrigin(0.5);
    this.playerSprites.set(playerId, sprite);
    return sprite;
  }

  private hidePlayerSprite(playerId: string): void {
    this.playerSprites.get(playerId)?.setVisible(false);
  }

  private createCultivatorAnimations(): void {
    for (const def of SKIN_DEFS) {
      if (!this.textures.exists(def.sheetKey)) {
        continue;
      }
      const frames = this.anims.generateFrameNumbers(def.sheetKey, {
        start: 0,
        end: Math.max(0, def.frameCount - 1),
      });
      if (!this.anims.exists(def.moveAnimationKey)) {
        this.anims.create({
          key: def.moveAnimationKey,
          frames,
          frameRate: CULTIVATOR_ANIMATION_FRAME_RATE,
          repeat: -1,
        });
      }
      if (!this.anims.exists(def.boostAnimationKey)) {
        this.anims.create({
          key: def.boostAnimationKey,
          frames,
          frameRate: CULTIVATOR_BOOST_ANIMATION_FRAME_RATE,
          repeat: -1,
        });
      }
    }
  }

  private drawCultivatorGlyph(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const layer = this.worldLayer;
    const size =
      CULTIVATOR_BASE_SIZE +
      getCultivatorVisualGrowth(player.level) * CULTIVATOR_LEVEL_SIZE_STEP;
    const alpha = player.isAlive ? (isSelf ? 0.96 : 0.82) : 0.38;
    const facing = player.facing;

    layer.fillStyle(0x0b0f15, 0.22 * alpha);
    this.fillLocalEllipse(player, -4, 8, size * 0.62, size * 0.34, 0, facing);

    layer.fillStyle(0xf5eee0, 0.74 * alpha);
    this.fillLocalEllipse(
      player,
      -8,
      -10,
      size * 0.18,
      size * 0.45,
      -0.58,
      facing,
    );
    this.fillLocalEllipse(
      player,
      -8,
      10,
      size * 0.18,
      size * 0.45,
      0.58,
      facing,
    );
    layer.fillStyle(0xd8d0c3, 0.58 * alpha);
    this.fillLocalEllipse(player, -12, 0, size * 0.22, size * 0.46, 0, facing);

    layer.fillStyle(0x141313, 0.9 * alpha);
    this.fillLocalEllipse(player, -4, 0, size * 0.22, size * 0.18, 0, facing);
    const hairBun = this.localPoint(player, -15, 0, facing);
    layer.fillCircle(hairBun.x, hairBun.y, size * 0.1);

    const leftSleeve = this.localPoint(player, -2, -size * 0.34, facing);
    const rightSleeve = this.localPoint(player, -2, size * 0.34, facing);
    layer.lineStyle(2, 0x111111, 0.42 * alpha);
    layer.lineBetween(player.x, player.y, leftSleeve.x, leftSleeve.y);
    layer.lineBetween(player.x, player.y, rightSleeve.x, rightSleeve.y);

    const swordStart = this.localPoint(player, size * 0.34, 0, facing);
    const swordTip = this.localPoint(player, size * 0.74, 0, facing);
    const swordLeft = this.localPoint(
      player,
      size * 0.54,
      -size * 0.055,
      facing,
    );
    const swordRight = this.localPoint(
      player,
      size * 0.54,
      size * 0.055,
      facing,
    );
    layer.lineStyle(3, 0xffd166, 0.76 * alpha);
    layer.lineBetween(swordStart.x, swordStart.y, swordTip.x, swordTip.y);
    layer.fillStyle(0xffe59c, 0.9 * alpha);
    layer.fillTriangle(
      swordTip.x,
      swordTip.y,
      swordLeft.x,
      swordLeft.y,
      swordRight.x,
      swordRight.y,
    );
  }

  private fillLocalEllipse(
    player: ServerSnapshotMessage["self"],
    localX: number,
    localY: number,
    radiusX: number,
    radiusY: number,
    localRotation: number,
    facing: number,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const points: Phaser.Math.Vector2[] = [];
    for (let index = 0; index < CULTIVATOR_ELLIPSE_SEGMENTS; index += 1) {
      const angle = (index / CULTIVATOR_ELLIPSE_SEGMENTS) * Math.PI * 2;
      const rotatedX =
        Math.cos(localRotation) * Math.cos(angle) * radiusX -
        Math.sin(localRotation) * Math.sin(angle) * radiusY;
      const rotatedY =
        Math.sin(localRotation) * Math.cos(angle) * radiusX +
        Math.cos(localRotation) * Math.sin(angle) * radiusY;
      points.push(
        this.localPoint(player, localX + rotatedX, localY + rotatedY, facing),
      );
    }
    this.worldLayer.fillPoints(points, true, true);
  }

  private localPoint(
    player: ServerSnapshotMessage["self"],
    localX: number,
    localY: number,
    facing: number,
  ): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(
      player.x + Math.cos(facing) * localX - Math.sin(facing) * localY,
      player.y + Math.sin(facing) * localX + Math.cos(facing) * localY,
    );
  }

  private drawSpiritStone(
    stone: ServerSnapshotMessage["visibleStones"][number],
  ): void {
    if (!this.worldLayer) {
      return;
    }
    // Direct flat draw: pick one of two precomputed tier styles by value, then
    // draw the shard/gem shape once — no LOD modes, aura, pulse or per-frame
    // style recompute. A fixed per-position rotation keeps stones looking
    // varied without any animation.
    const style =
      stone.value >= CONDENSED_STONE_MIN_VALUE
        ? CONDENSED_STONE_STYLE
        : FRAGMENT_STONE_STYLE;
    const rotation =
      ((Math.sin(stone.x * 12.9898 + stone.y * 78.233) + 1) * Math.PI) %
      Math.PI;
    this.drawSpiritStoneShape(stone.x, stone.y, style.radius, rotation, style);
  }

  private drawSpiritStoneShape(
    x: number,
    y: number,
    radius: number,
    rotation: number,
    style: SpiritStoneVisualStyle,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    this.worldLayer.fillStyle(style.color, style.alpha);
    // Q 版 chunky-gem look: a thick dark-ink outline (bold flat block) instead
    // of the old thin same-colour ring.
    this.worldLayer.lineStyle(
      style.outlineWidth,
      style.outlineColor,
      style.outlineAlpha,
    );

    switch (style.shape) {
      case "shard":
        this.fillSpiritStonePolygon(x, y, radius, rotation, [
          [0, -1],
          [0.62, -0.18],
          [0.26, 0.88],
          [-0.48, 0.36],
          [-0.34, -0.64],
        ]);
        break;
      case "diamond":
        this.fillSpiritStonePolygon(x, y, radius, rotation, [
          [0, -1.12],
          [0.68, 0],
          [0, 1.12],
          [-0.68, 0],
        ]);
        break;
      case "octagon":
        this.fillSpiritStonePolygon(x, y, radius, rotation, [
          [-0.36, -1],
          [0.36, -1],
          [0.92, -0.36],
          [0.92, 0.36],
          [0.36, 1],
          [-0.36, 1],
          [-0.92, 0.36],
          [-0.92, -0.36],
        ]);
        break;
      case "cluster":
        this.fillSpiritStonePolygon(
          x - radius * 0.38,
          y + radius * 0.18,
          radius * 0.48,
          rotation + 0.34,
          [
            [0, -1],
            [0.72, 0.12],
            [0, 0.92],
            [-0.62, 0.05],
          ],
        );
        this.fillSpiritStonePolygon(
          x + radius * 0.42,
          y + radius * 0.16,
          radius * 0.42,
          rotation - 0.28,
          [
            [0, -1],
            [0.64, 0.08],
            [0, 0.88],
            [-0.56, 0.04],
          ],
        );
        this.worldLayer.fillStyle(style.color, style.alpha);
        this.fillSpiritStonePolygon(x, y - radius * 0.08, radius, rotation, [
          [0, -1.15],
          [0.72, -0.22],
          [0.42, 0.96],
          [-0.46, 0.86],
          [-0.76, -0.18],
        ]);
        break;
      case "marrow":
        this.fillSpiritStonePolygon(x, y, radius, rotation, [
          [0, -1.18],
          [0.76, -0.48],
          [0.92, 0.34],
          [0.22, 1.08],
          [-0.62, 0.84],
          [-0.9, 0],
          [-0.56, -0.78],
        ]);
        break;
      case "crystal":
        this.fillSpiritStonePolygon(x, y, radius, rotation, [
          [0, -1.12],
          [0.76, -0.18],
          [0.46, 0.96],
          [-0.46, 0.96],
          [-0.76, -0.18],
        ]);
        break;
    }

    if (style.coreAlpha > 0) {
      this.worldLayer.fillStyle(style.coreColor, style.coreAlpha);
      this.worldLayer.fillCircle(x, y, Math.max(1.5, radius * 0.34));
    }
  }

  private fillSpiritStonePolygon(
    x: number,
    y: number,
    radius: number,
    rotation: number,
    points: readonly (readonly [number, number])[],
  ): void {
    const first = points[0];
    if (!this.worldLayer || !first) {
      return;
    }

    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const [firstX, firstY] = first;
    this.worldLayer.beginPath();
    this.worldLayer.moveTo(
      x + (firstX * cos - firstY * sin) * radius,
      y + (firstX * sin + firstY * cos) * radius,
    );
    for (const [pointX, pointY] of points.slice(1)) {
      this.worldLayer.lineTo(
        x + (pointX * cos - pointY * sin) * radius,
        y + (pointX * sin + pointY * cos) * radius,
      );
    }
    this.worldLayer.closePath();
    this.worldLayer.fillPath();
    this.worldLayer.strokePath();
  }

  private drawPlayerLabel(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): void {
    const label = this.getPlayerLabel(player.id, isSelf);
    // Authoritative colour each frame: team colour in team matches (so
    // teammates/enemies read instantly and survive respawn), else the default
    // self/other colour. Never leaves a stale colour on the cached label.
    label.setColor(
      teamLabelColor(player.teamId) ?? (isSelf ? "#f7e4b5" : "#d7f6ff"),
    );
    label
      .setText(displayPlayerName(player.name, this.getLocale()))
      .setPosition(player.x, player.y - player.radius - PLAYER_LABEL_OFFSET)
      .setAlpha(player.isAlive ? 1 : 0.55)
      .setVisible(true);
  }

  private getPlayerLabel(
    playerId: string,
    isSelf: boolean,
  ): Phaser.GameObjects.Text {
    const existing = this.playerLabels.get(playerId);
    if (existing) {
      // Colour is owned by drawPlayerLabel (team-aware); don't reset it here.
      return existing;
    }

    const label = this.add
      .text(0, 0, "", {
        color: isSelf ? "#f7e4b5" : "#d7f6ff",
        fontFamily:
          "'Avenir Next', 'Segoe UI', 'PingFang SC', 'Noto Sans SC', sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        stroke: "#07101d",
        strokeThickness: 4,
      })
      .setDepth(8)
      .setOrigin(0.5, 1);
    this.playerLabels.set(playerId, label);
    return label;
  }

  private hideStalePlayerLabels(visiblePlayerIds: Set<string>): void {
    for (const [playerId, label] of this.playerLabels) {
      if (!visiblePlayerIds.has(playerId)) {
        label.setVisible(false);
      }
    }
  }

  private hideStaleTexturedFormationSprites(
    visiblePlayerIds: Set<string>,
  ): void {
    for (const [key, sprite] of this.texturedFormationSprites) {
      if (!visiblePlayerIds.has(readTexturedFormationSpritePlayerId(key))) {
        sprite.setVisible(false);
      }
    }
  }

  private hideTexturedFormationSpritesForPlayer(playerId: string): void {
    for (const [key, sprite] of this.texturedFormationSprites) {
      if (readTexturedFormationSpritePlayerId(key) === playerId) {
        sprite.setVisible(false);
      }
    }
  }

  private clearPlayerLabels(): void {
    for (const label of this.playerLabels.values()) {
      label.destroy();
    }
    this.playerLabels.clear();
    for (const sprite of this.texturedFormationSprites.values()) {
      sprite.destroy();
    }
    this.texturedFormationSprites.clear();
  }

  private hideStalePlayerSprites(visiblePlayerIds: Set<string>): void {
    for (const [playerId, sprite] of this.playerSprites) {
      if (!visiblePlayerIds.has(playerId)) {
        sprite.setVisible(false);
      }
    }
  }

  private clearPlayerSprites(): void {
    for (const sprite of this.playerSprites.values()) {
      sprite.destroy();
    }
    this.playerSprites.clear();
  }

  private drawAttackArc(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): void {
    if (!this.worldLayer) {
      return;
    }
    if (!player.isAlive) {
      this.hideTexturedFormationSpritesForPlayer(player.id);
      return;
    }

    const style = getAttackFormationStyle(
      player.attackPhase,
      isSelf,
      player.level,
    );
    const startAngle = player.facing - player.attackArcRadians / 2;
    const endAngle = player.facing + player.attackArcRadians / 2;

    if (this.shouldUseSimplifiedFormation(player, isSelf)) {
      this.hideTexturedFormationSpritesForPlayer(player.id);
      this.drawSimplifiedMobileFormation(player, startAngle, endAngle, style);
      return;
    }

    const usesTexturedBase = this.drawTexturedFormationBase(
      player,
      style,
      isSelf,
    );
    if (!usesTexturedBase) {
      this.hideTexturedFormationSpritesForPlayer(player.id);
    }

    if (player.attackPhase === "recovery") {
      this.drawRecoveryAttackArc(player, startAngle, endAngle, style, isSelf);
    } else {
      this.drawDangerAttackArc(player, startAngle, endAngle, style, isSelf);
    }

    if (player.attackPhase !== "ready") {
      const pulseRadius =
        player.radius + 5 + Math.min(10, player.attackRemainingMs / 80);
      this.worldLayer.lineStyle(3, style.frontArcColor, 0.8);
      this.worldLayer.strokeCircle(player.x, player.y, pulseRadius);
    }
  }

  private shouldUseSimplifiedFormation(
    player: ServerSnapshotMessage["self"],
    isSelf: boolean,
  ): boolean {
    if (isSelf || player.attackPhase !== "ready") {
      return false;
    }
    const focus = this.getRenderFocusPoint();
    if (!focus) {
      return false;
    }
    const distance = Math.hypot(player.x - focus.x, player.y - focus.y);
    if (!this.isMobileRuntime()) {
      const viewportDistance = Math.max(
        DESKTOP_FAR_FORMATION_MIN_DISTANCE,
        (this.scale.width / this.cameras.main.zoom) * 0.68,
      );
      return distance > viewportDistance;
    }
    const viewportDistance = Math.max(
      MOBILE_FAR_FORMATION_MIN_DISTANCE,
      this.scale.width / this.cameras.main.zoom / 2,
    );
    return distance > viewportDistance;
  }

  private drawSimplifiedMobileFormation(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
  ): void {
    if (!this.worldLayer) {
      return;
    }
    this.worldLayer.lineStyle(1.4, style.backRingColor, style.backRingAlpha);
    this.worldLayer.strokeCircle(player.x, player.y, player.attackRadius);
    this.drawAttackSectorInnerArcs(player, startAngle, endAngle, style, true, {
      maxArcCount: 1,
      alphaMultiplier: 0.72,
    });
    this.drawAttackArcLine(player, player.attackRadius, startAngle, endAngle, {
      alpha: Math.min(0.72, style.frontArcAlpha),
      color: style.frontArcColor,
      width: 2.6,
    });
  }

  private drawTexturedFormationBase(
    player: ServerSnapshotMessage["self"],
    style: AttackFormationStyle,
    isSelf: boolean,
  ): boolean {
    const focus = this.getRenderFocusPoint();
    const distanceToSelf = focus
      ? Math.hypot(player.x - focus.x, player.y - focus.y)
      : 0;
    const layerLimit = getFormationLayerLimit(player, {
      isSelf,
      isMobile: this.isMobileRuntime(),
      distanceToSelf,
      cameraZoom: this.cameras.main.zoom,
      visiblePlayerCount:
        (this.viewState?.lastSnapshot?.visiblePlayers.length ?? 0) + 1,
      debugLayerLimit: this.debugFormationLayerLimit,
    });
    if (layerLimit <= 0) {
      this.hideTexturedFormationSpritesForPlayer(player.id);
      return false;
    }

    const realmKey = getFormationRealmKey(player.level);
    const scale = player.attackRadius / TEXTURED_FORMATION_SOURCE_RADIUS;
    const phaseAlpha = player.attackPhase === "recovery" ? 0.58 : 1;
    const windupPulse =
      player.attackPhase === "windup"
        ? 1 + Math.sin(this.time.now * 0.018) * 0.08
        : 1;
    const rotations = [
      this.time.now * 0.00008,
      player.facing - this.time.now * 0.00016,
      this.time.now * 0.00024,
    ] as const;

    for (const [index, layer] of TEXTURED_FORMATION_LAYER_DEFS.entries()) {
      if (index >= layerLimit) {
        this.getTexturedFormationSprite(player.id, index).setVisible(false);
        continue;
      }
      const sprite = this.getTexturedFormationSprite(player.id, index);
      sprite
        .setTexture(createTexturedFormationTextureKey(realmKey, index))
        .setPosition(player.x, player.y)
        .setRotation(rotations[index] ?? 0)
        .setScale(scale * (index === 1 ? windupPulse : 1))
        .setTint(
          player.attackPhase === "windup" ? style.frontArcColor : 0xffffff,
        )
        .setAlpha(layer.alpha * phaseAlpha * (player.isAlive ? 1 : 0.38))
        .setVisible(true);
    }

    return true;
  }

  private getTexturedFormationSprite(
    playerId: string,
    layerIndex: number,
  ): Phaser.GameObjects.Image {
    const key = createTexturedFormationSpriteKey(playerId, layerIndex);
    const existing = this.texturedFormationSprites.get(key);
    if (existing) {
      return existing;
    }

    const layer =
      TEXTURED_FORMATION_LAYER_DEFS[layerIndex] ??
      TEXTURED_FORMATION_LAYER_DEFS[0];
    const sprite = this.add
      .image(0, 0, createTexturedFormationTextureKey("lianqi", layerIndex))
      .setDepth(layer.depth)
      .setOrigin(0.5, 0.5)
      .setVisible(false);
    this.texturedFormationSprites.set(key, sprite);
    return sprite;
  }

  private createFlyingSwordPlaceholderTexture(): void {
    if (this.textures.exists(FLYING_SWORD_TEXTURE_KEY)) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = FLYING_SWORD_TEXTURE_SIZE;
    canvas.height = FLYING_SWORD_TEXTURE_SIZE;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const center = FLYING_SWORD_TEXTURE_SIZE / 2;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineCap = "round";
    context.lineJoin = "round";

    const glow = context.createLinearGradient(14, center, 86, center);
    glow.addColorStop(0, "rgba(120, 220, 255, 0)");
    glow.addColorStop(0.52, "rgba(255, 232, 166, 0.34)");
    glow.addColorStop(1, "rgba(255, 244, 210, 0)");
    context.fillStyle = glow;
    context.beginPath();
    context.ellipse(center + 4, center, 38, 13, 0, 0, Math.PI * 2);
    context.fill();

    const blade = new Path2D();
    blade.moveTo(86, center);
    blade.lineTo(48, center - 8);
    blade.lineTo(18, center - 3);
    blade.lineTo(11, center);
    blade.lineTo(18, center + 3);
    blade.lineTo(48, center + 8);
    blade.closePath();
    context.fillStyle = "rgba(255, 255, 255, 0.94)";
    context.fill(blade);
    context.strokeStyle = "rgba(255, 231, 151, 0.94)";
    context.lineWidth = 3;
    context.stroke(blade);

    context.beginPath();
    context.moveTo(21, center);
    context.lineTo(74, center);
    context.strokeStyle = "rgba(86, 231, 255, 0.88)";
    context.lineWidth = 2.2;
    context.stroke();

    context.beginPath();
    context.moveTo(22, center - 8);
    context.lineTo(10, center - 13);
    context.moveTo(22, center + 8);
    context.lineTo(10, center + 13);
    context.strokeStyle = "rgba(21, 28, 42, 0.72)";
    context.lineWidth = 4;
    context.stroke();

    this.textures.addCanvas(FLYING_SWORD_TEXTURE_KEY, canvas);
  }

  private drawAttackArcLine(
    player: ServerSnapshotMessage["self"],
    radius: number,
    startAngle: number,
    endAngle: number,
    line: { color: number; alpha: number; width: number },
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const points = this.createArcPoints(player, radius, startAngle, endAngle);
    this.worldLayer.lineStyle(line.width, line.color, line.alpha);
    this.worldLayer.strokePoints(points, false, false);
  }

  private drawDangerAttackArc(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    isSelf: boolean,
  ): void {
    const isWindup = player.attackPhase === "windup";
    this.drawAttackSectorInnerArcs(player, startAngle, endAngle, style, isSelf);
    if (!isSelf) {
      this.drawAttackArcLine(
        player,
        player.attackRadius + 1.5,
        startAngle,
        endAngle,
        {
          alpha: Math.min(0.42, style.frontArcAlpha * 0.42),
          color: style.boundaryColor,
          width: isWindup ? 7.2 : 5.8,
        },
      );
    }

    this.drawAttackArcLine(player, player.attackRadius, startAngle, endAngle, {
      alpha: isSelf
        ? style.frontArcAlpha
        : Math.min(0.9, style.frontArcAlpha + 0.2),
      color: style.frontArcColor,
      width: isSelf ? (isWindup ? 3.5 : 2.4) : isWindup ? 4.4 : 3.4,
    });
  }

  private drawAttackSectorInnerArcs(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    isSelf: boolean,
    options: { maxArcCount?: number; alphaMultiplier?: number } = {},
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const isWindup = player.attackPhase === "windup";
    const realmTier = getCultivatorVisualTier(player.level);
    const arcCount = Math.min(
      options.maxArcCount ?? ATTACK_SECTOR_INNER_ARC_RATIOS.length,
      Math.max(1, realmTier >= 3 ? 3 : realmTier >= 2 ? 2 : 1),
    );
    const alphaMultiplier = options.alphaMultiplier ?? 1;
    const ownerAlpha = isSelf ? 0.74 : 1;
    const windupPulse = isWindup
      ? 0.78 + Math.sin(this.time.now * 0.014) * 0.22
      : 1;
    const phaseAlpha = isWindup ? 1.32 : 1;

    for (let index = 0; index < arcCount; index += 1) {
      const ratio = ATTACK_SECTOR_INNER_ARC_RATIOS[index] ?? 0.62;
      const radius = player.attackRadius * ratio;
      const depthRatio = index / Math.max(1, arcCount - 1);
      const windupCascade = isWindup
        ? Phaser.Math.Clamp(
            windupPulse + depthRatio * 0.24 - index * 0.08,
            0.64,
            1.28,
          )
        : 1;
      const alpha =
        style.innerAlpha *
        alphaMultiplier *
        ownerAlpha *
        phaseAlpha *
        windupCascade *
        Phaser.Math.Linear(0.76, 1.08, depthRatio);
      const width =
        (isWindup ? 2.2 : 1.35) + depthRatio * (isSelf ? 0.36 : 0.58);

      if (!isSelf || isWindup) {
        this.drawAttackArcLine(player, radius, startAngle, endAngle, {
          alpha: Math.min(0.22, alpha * 0.36),
          color: style.boundaryColor,
          width: width + (isWindup ? 3.4 : 2.1),
        });
      }

      this.drawAttackArcLine(player, radius, startAngle, endAngle, {
        alpha: Math.min(0.74, alpha),
        color: index === arcCount - 1 ? style.frontArcColor : style.innerColor,
        width,
      });
    }
  }

  private drawRecoveryAttackArc(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    isSelf: boolean,
  ): void {
    const recoveryProgress = getAttackRecoveryProgress(player);
    const segmentCount = player.attackLastResult === "miss" ? 4 : 6;
    const gapRatio = Phaser.Math.Linear(0.42, 0.16, recoveryProgress);
    const segmentRadians = (endAngle - startAngle) / segmentCount;

    this.drawRecoveryDisabledBand(
      player,
      startAngle,
      endAngle,
      style,
      recoveryProgress,
      isSelf,
    );
    this.drawRecoveryInnerArcs(
      player,
      startAngle,
      endAngle,
      style,
      recoveryProgress,
      isSelf,
    );

    for (let index = 0; index < segmentCount; index += 1) {
      const segmentStart = startAngle + segmentRadians * (index + gapRatio / 2);
      const segmentEnd =
        startAngle + segmentRadians * (index + 1 - gapRatio / 2);
      this.drawAttackArcLine(
        player,
        player.attackRadius,
        segmentStart,
        segmentEnd,
        {
          alpha:
            style.frontArcAlpha *
            Phaser.Math.Linear(0.72, 1.1, recoveryProgress),
          color: style.frontArcColor,
          width: Phaser.Math.Linear(1.8, isSelf ? 2.6 : 3.4, recoveryProgress),
        },
      );
    }

    if (recoveryProgress > 0.18) {
      const restoreEnd =
        startAngle + (endAngle - startAngle) * recoveryProgress;
      this.drawAttackArcLine(
        player,
        player.attackRadius + 2.4,
        startAngle,
        restoreEnd,
        {
          alpha: (isSelf ? 0.34 : 0.5) * recoveryProgress,
          color: style.boundaryColor,
          width: isSelf ? 2.2 : 3,
        },
      );
    }

    if (recoveryProgress < 0.36) {
      this.drawAttackResultArcFlash(
        player,
        startAngle,
        endAngle,
        style,
        recoveryProgress,
        isSelf,
      );
    }
  }

  private drawRecoveryInnerArcs(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    recoveryProgress: number,
    isSelf: boolean,
  ): void {
    const realmTier = getCultivatorVisualTier(player.level);
    const arcCount = Math.max(1, realmTier >= 3 ? 3 : realmTier >= 2 ? 2 : 1);
    const segmentCount = player.attackLastResult === "miss" ? 3 : 4;
    const segmentRadians = (endAngle - startAngle) / segmentCount;
    const baseAlpha = (isSelf ? 0.1 : 0.18) * (1 - recoveryProgress * 0.72);

    for (let arcIndex = 0; arcIndex < arcCount; arcIndex += 1) {
      const ratio = ATTACK_SECTOR_INNER_ARC_RATIOS[arcIndex] ?? 0.62;
      const radius = player.attackRadius * ratio;
      const extinguish = Phaser.Math.Clamp(
        1 - (recoveryProgress - arcIndex * 0.16) / 0.64,
        0,
        1,
      );
      for (let index = 0; index < segmentCount; index += 1) {
        const gap = player.attackLastResult === "miss" ? 0.34 : 0.22;
        const segmentStart = startAngle + segmentRadians * (index + gap);
        const segmentEnd = startAngle + segmentRadians * (index + 1 - gap);
        this.drawAttackArcLine(player, radius, segmentStart, segmentEnd, {
          alpha: baseAlpha * extinguish,
          color:
            player.attackLastResult === "miss" ? 0x8b96a8 : style.runeColor,
          width: 1 + arcIndex * 0.22,
        });
      }
    }
  }

  private drawAttackResultArcFlash(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    recoveryProgress: number,
    isSelf: boolean,
  ): void {
    if (player.attackLastResult !== "hit") {
      return;
    }

    const flashAlpha = (isSelf ? 0.38 : 0.62) * (1 - recoveryProgress / 0.36);
    const centerAngle = Phaser.Math.Angle.Wrap((startAngle + endAngle) / 2);
    const flashWidth = Math.max(0.16, (endAngle - startAngle) * 0.18);
    const flashStart = centerAngle - flashWidth;
    const flashEnd = centerAngle + flashWidth;
    for (const ratio of [0.62, 0.78, 1]) {
      this.drawAttackArcLine(
        player,
        player.attackRadius * ratio,
        flashStart,
        flashEnd,
        {
          alpha: flashAlpha * (ratio === 1 ? 1 : 0.72),
          color: ratio === 1 ? 0xfff1a8 : style.innerColor,
          width: ratio === 1 ? 3.4 : 2,
        },
      );
    }
  }

  private drawRecoveryDisabledBand(
    player: ServerSnapshotMessage["self"],
    startAngle: number,
    endAngle: number,
    style: AttackFormationStyle,
    recoveryProgress: number,
    isSelf: boolean,
  ): void {
    if (!this.worldLayer) {
      return;
    }

    const alpha = (isSelf ? 0.12 : 0.22) * (1 - recoveryProgress * 0.55);
    this.drawAttackArcLine(
      player,
      player.attackRadius - 2,
      startAngle,
      endAngle,
      {
        alpha,
        color: 0x6b7280,
        width: isSelf ? 5.5 : 7.5,
      },
    );

    const slashCount = player.attackLastResult === "miss" ? 3 : 4;
    for (let index = 0; index < slashCount; index += 1) {
      const t = (index + 0.5) / slashCount;
      const angle = Phaser.Math.Linear(startAngle, endAngle, t);
      const radius = player.attackRadius - 8;
      const centerX = player.x + Math.cos(angle) * radius;
      const centerY = player.y + Math.sin(angle) * radius;
      const tangentX = Math.cos(angle + Math.PI / 2);
      const tangentY = Math.sin(angle + Math.PI / 2);
      const length = 8 + player.level * 0.28;
      this.worldLayer.lineStyle(1.6, style.frontArcColor, alpha * 2.4);
      this.worldLayer.lineBetween(
        centerX - tangentX * length,
        centerY - tangentY * length,
        centerX + tangentX * length,
        centerY + tangentY * length,
      );
    }
  }

  private createArcPoints(
    player: ServerSnapshotMessage["self"],
    radius: number,
    startAngle: number,
    endAngle: number,
  ): Phaser.Math.Vector2[] {
    const points: Phaser.Math.Vector2[] = [];
    for (let index = 0; index <= ATTACK_ARC_SEGMENTS; index += 1) {
      const progress = index / ATTACK_ARC_SEGMENTS;
      const angle = Phaser.Math.Linear(startAngle, endAngle, progress);
      points.push(
        new Phaser.Math.Vector2(
          player.x + Math.cos(angle) * radius,
          player.y + Math.sin(angle) * radius,
        ),
      );
    }
    return points;
  }

  private updateHint(): void {
    if (!this.hintText) {
      return;
    }

    this.hintText.setText(
      formatControlHint(
        this.getLocale(),
        this.mobileControls?.isMobileEnabled() ? "mobile" : "desktop",
      ),
    );
  }

  private sendInputIfReady(time: number): void {
    if (
      !this.viewState?.welcome ||
      !this.viewState.lastSnapshot?.self?.isAlive
    ) {
      return;
    }
    if (time < this.nextInputAt) {
      return;
    }

    const angle = this.resolveInputAngle();
    const boost = this.resolveBoostInput();

    if (
      Math.abs(angle - this.lastSentAngle) < 0.025 &&
      boost === this.lastBoost &&
      time - this.nextInputAt < INPUT_SEND_INTERVAL_MS * 4
    ) {
      this.nextInputAt = time + INPUT_SEND_INTERVAL_MS;
      return;
    }

    this.realtimeClient.sendInput(angle, boost);
    this.lastSentAngle = angle;
    this.lastBoost = boost;
    this.nextInputAt = time + INPUT_SEND_INTERVAL_MS;
  }

  private resolveBoostInput(): boolean {
    if (this.mobileControls?.isMobileEnabled()) {
      return (
        (this.boostKey?.isDown ?? false) || this.mobileControls.isBoostActive()
      );
    }
    const pointer = this.input.activePointer;
    return (
      (this.boostKey?.isDown ?? false) ||
      pointer.primaryDown ||
      pointer.leftButtonDown()
    );
  }

  private resolveInputAngle(): number {
    const self = this.viewState?.lastSnapshot?.self;
    const mobileJoystickAngle = this.mobileControls?.getJoystickAngle();
    if (mobileJoystickAngle !== null && mobileJoystickAngle !== undefined) {
      if (this.mouseLockActive) {
        this.exitMouseLock();
      }
      this.controlMode = "mobile";
      this.keyboardLockedAngle = null;
      this.previousKeyboardTurnDirection = 0;
      return mobileJoystickAngle;
    }
    if (this.mobileControls?.isMobileEnabled()) {
      return self?.facing ?? this.lastSentAngle;
    }

    const turnLeft = this.leftKey?.isDown ?? false;
    const turnRight = this.rightKey?.isDown ?? false;
    const pointer = this.input.activePointer;
    if (self && turnLeft !== turnRight) {
      if (this.mouseLockActive) {
        this.exitMouseLock();
      }
      this.controlMode = "keyboard";
      this.keyboardLockedAngle = null;
      this.previousKeyboardTurnDirection = turnLeft ? -1 : 1;
      this.lastPointerPosition = { x: pointer.x, y: pointer.y };
      return (
        self.facing +
        (turnLeft
          ? -KEYBOARD_STEER_TARGET_OFFSET
          : KEYBOARD_STEER_TARGET_OFFSET)
      );
    }

    if (this.previousKeyboardTurnDirection !== 0) {
      this.keyboardLockedAngle = self?.facing ?? this.lastSentAngle;
      this.previousKeyboardTurnDirection = 0;
    }

    if (this.mouseLockActive && this.lockedCursorPosition) {
      this.controlMode = "mouse";
      this.keyboardLockedAngle = null;
      const centerX = this.scale.width / 2;
      const centerY = this.scale.height / 2;
      const deltaX = this.lockedCursorPosition.x - centerX;
      const deltaY = this.lockedCursorPosition.y - centerY;
      if (Math.hypot(deltaX, deltaY) < LOCKED_CURSOR_MIN_DISTANCE && self) {
        return self.facing;
      }
      return Math.atan2(deltaY, deltaX);
    }

    if (this.hasPointerMoved(pointer)) {
      this.controlMode = "mouse";
      this.keyboardLockedAngle = null;
    }
    this.lastPointerPosition = { x: pointer.x, y: pointer.y };

    if (self && this.controlMode === "keyboard") {
      return this.keyboardLockedAngle ?? self.facing;
    }

    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    return Math.atan2(pointer.y - centerY, pointer.x - centerX);
  }

  private hasPointerMoved(pointer: Phaser.Input.Pointer): boolean {
    if (!this.lastPointerPosition) {
      return false;
    }
    return (
      Math.hypot(
        pointer.x - this.lastPointerPosition.x,
        pointer.y - this.lastPointerPosition.y,
      ) > POINTER_MODE_MOVE_THRESHOLD
    );
  }

  private updateGrowthFeedback(self: ServerSnapshotMessage["self"]): void {
    if (!self.isAlive) {
      this.lastSelfLevel = null;
      return;
    }
    if (this.lastSelfLevel !== null && self.level > this.lastSelfLevel) {
      this.playAudioCueKind("level_up");
    }
    this.lastSelfLevel = self.level;
  }
}

const TEAM_LABEL_COLORS: Record<string, string> = {
  azure: "#6db6ff",
  crimson: "#ff7a7a",
};

function teamLabelColor(teamId: string | null | undefined): string | null {
  return teamId ? (TEAM_LABEL_COLORS[teamId] ?? null) : null;
}

function displayPlayerName(playerName: string, locale: Locale): string {
  return playerName.trim().length > 0
    ? playerName
    : formatDefaultPlayerName(locale);
}

function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function getSwordCountForLevel(level: number): number {
  const realmTier = Math.max(1, Math.ceil(Math.floor(level) / 10));
  const launchRingCount = Phaser.Math.Clamp(realmTier + 1, 2, 5);
  return launchRingCount * SWORDS_PER_LAUNCH_RING;
}

function getSwordCascadeDelay(index: number, swordCount: number): number {
  if (swordCount <= 1) {
    return 0;
  }

  return Math.min(
    SWORD_SLASH_CASCADE_MAX_MS,
    index * SWORD_SLASH_CASCADE_STEP_MS,
  );
}

function getSwordLaunchPoint(
  player: ServerSnapshotMessage["self"],
  swordIndex: number,
): { x: number; y: number } {
  const realmTier = getCultivatorVisualTier(player.level);
  const ringRadius = getFormationLayerRingRadius(
    player.attackRadius,
    realmTier,
  );
  const centerRadius = Math.max(
    ringRadius + 8,
    player.attackRadius -
      ringRadius -
      getFormationLayerRingEdgeInset(realmTier),
  );
  const visibleLaunchRadius = Math.min(
    centerRadius,
    Phaser.Math.Clamp(player.attackRadius * 0.38, player.radius + 32, 130),
  );
  const ringIndex = getSwordLaunchRingIndex(
    Math.floor(swordIndex / SWORDS_PER_LAUNCH_RING),
  );
  const angle =
    player.facing + (ringIndex / FORMATION_LAYER_RING_COUNT) * Math.PI * 2;
  const pairOffset =
    (swordIndex % SWORDS_PER_LAUNCH_RING) - (SWORDS_PER_LAUNCH_RING - 1) / 2;
  const tangentAngle = angle + Math.PI / 2;
  const tangentOffset = pairOffset * 12;
  return {
    x:
      player.x +
      Math.cos(angle) * visibleLaunchRadius +
      Math.cos(tangentAngle) * tangentOffset,
    y:
      player.y +
      Math.sin(angle) * visibleLaunchRadius +
      Math.sin(tangentAngle) * tangentOffset,
  };
}

function getMissReturnSwordPose(
  player: ServerSnapshotMessage["self"],
  swordIndex: number,
  launchPoint: { x: number; y: number },
  forwardEnd: { x: number; y: number },
  forwardAngle: number,
  swordElapsedMs: number,
): { x: number; y: number; angle: number } | null {
  if (swordElapsedMs <= SWORD_SLASH_TRAVEL_MS) {
    return null;
  }

  const progress = Phaser.Math.Clamp(
    (swordElapsedMs - SWORD_SLASH_TRAVEL_MS) / SWORD_MISS_RETURN_MS,
    0,
    1,
  );
  const sideSign = hashString(player.id) % 2 === 0 ? 1 : -1;
  const returnRadius = player.radius + 36 + (swordIndex % 3) * 7;
  const startAngle = forwardAngle;
  const endAngle = player.facing + (swordIndex % SWORDS_PER_LAUNCH_RING) * 0.12;
  const sweptAngle = startAngle + sideSign * Math.PI * 1.35;
  const lineProgress = Phaser.Math.Clamp(progress / 0.24, 0, 1);
  const orbitProgress = Phaser.Math.Clamp((progress - 0.18) / 0.82, 0, 1);
  const orbitEased = 1 - (1 - orbitProgress) ** 2;
  const orbitAngle = Phaser.Math.Linear(sweptAngle, endAngle, orbitEased);
  const orbitPoint = {
    x: player.x + Math.cos(orbitAngle) * returnRadius,
    y: player.y + Math.sin(orbitAngle) * returnRadius,
  };
  const lineEnd = {
    x: player.x + Math.cos(sweptAngle) * returnRadius,
    y: player.y + Math.sin(sweptAngle) * returnRadius,
  };
  const point =
    progress < 0.18
      ? {
          x: Phaser.Math.Linear(forwardEnd.x, lineEnd.x, lineProgress),
          y: Phaser.Math.Linear(forwardEnd.y, lineEnd.y, lineProgress),
        }
      : orbitPoint;
  const tangentAngle =
    progress < 0.18
      ? Math.atan2(lineEnd.y - forwardEnd.y, lineEnd.x - forwardEnd.x)
      : orbitAngle + (sideSign * Math.PI) / 2;
  return {
    x: point.x,
    y: point.y,
    angle: tangentAngle,
  };
}

function getSwordLaunchRingIndex(swordIndex: number): number {
  const order = [0, 1, 4, 2, 3];
  return order[swordIndex % order.length] ?? 0;
}

function getFlyingSwordTint(style: FlyingSwordVisualStyle): number {
  if (style.bright) {
    return style.tone === "slash" ? 0xfff1a8 : 0xfff7d2;
  }
  return style.tone === "windup" ? 0x9ce6ff : 0xd7f8ff;
}

function getFlyingSwordDisplayWidth(
  length: number,
  tone: FlyingSwordTone,
): number {
  return length * (tone === "slash" ? 1.95 : 1.62) * FLYING_SWORD_DISPLAY_SCALE;
}

function getAttackEffectVisualDuration(
  effect: AttackEffect,
  attackerLevel: number,
): number {
  const isLowTier = attackerLevel <= LOW_TIER_ATTACK_LEVEL_MAX;
  if (effect.result === "started") {
    return effect.durationMs;
  }

  const swordCount = getSwordCountForLevel(attackerLevel);
  const cascadeDuration =
    getSwordCascadeDelay(swordCount - 1, swordCount) +
    SWORD_SLASH_TRAVEL_MS +
    (effect.result === "missed" ? SWORD_MISS_RETURN_MS : 72);
  return Math.max(
    Math.min(SWORD_SLASH_MS, effect.durationMs),
    cascadeDuration,
    isLowTier ? SWORD_LOW_TIER_SLASH_VISUAL_MIN_MS : SWORD_SLASH_VISUAL_MIN_MS,
  );
}

function getAttackEffectPoint(
  effect: AttackEffect,
  role: "attacker" | "target",
  fallback: ServerSnapshotMessage["self"] | null,
): { x: number; y: number } | null {
  const x = role === "attacker" ? effect.attackerX : effect.targetX;
  const y = role === "attacker" ? effect.attackerY : effect.targetY;
  if (typeof x === "number" && typeof y === "number") {
    return { x, y };
  }
  return fallback ? { x: fallback.x, y: fallback.y } : null;
}

function getMissDestination(
  origin: { x: number; y: number },
  facing: number,
  attackRadius: number,
): { x: number; y: number } {
  return {
    x: origin.x + Math.cos(facing) * attackRadius,
    y: origin.y + Math.sin(facing) * attackRadius,
  };
}

function getAttackRecoveryProgress(
  player: ServerSnapshotMessage["self"],
): number {
  const fallbackDuration = player.attackLastResult === "miss" ? 760 : 1040;
  if (player.attackRemainingMs <= 0) {
    return 1;
  }
  return Phaser.Math.Clamp(
    1 - player.attackRemainingMs / fallbackDuration,
    0,
    1,
  );
}

function getCultivatorVisualTier(level: number): number {
  if (!Number.isFinite(level) || level <= 1) {
    return 1;
  }
  return Math.max(1, Math.min(5, Math.ceil(level / 10)));
}

function getFormationLayerRingRadius(
  attackRadius: number,
  realmTier: number,
): number {
  const ratioByTier = [0, 0.19, 0.2, 0.17, 0.17, 0.17];
  const ratio = ratioByTier[Math.max(1, Math.min(5, realmTier))] ?? 0.095;
  return Phaser.Math.Clamp(attackRadius * ratio, 10, attackRadius * 0.26);
}

function getFormationLayerRingEdgeInset(realmTier: number): number {
  return realmTier === 1 ? 5 : 12;
}

function createTexturedFormationSpriteKey(
  playerId: string,
  layerIndex: number,
): string {
  return `${playerId}::formation-base::${layerIndex.toString()}`;
}

function createTexturedFormationTextureKey(
  realmKey: FormationRealmKey,
  layerIndex: number,
): string {
  return `formation-base-${realmKey}-layer-${(layerIndex + 1).toString()}`;
}

function readTexturedFormationSpritePlayerId(key: string): string {
  return key.split("::", 1)[0] ?? key;
}

function countVisibleGameObjects(
  gameObjects: Iterable<Phaser.GameObjects.GameObject>,
): number {
  let count = 0;
  for (const gameObject of gameObjects) {
    const visible = (gameObject as { visible?: boolean }).visible;
    if (visible !== false) {
      count += 1;
    }
  }
  return count;
}

function readHeapUsedMb(): number | null {
  const memory = (
    performance as Performance & {
      memory?: { usedJSHeapSize?: number };
    }
  ).memory;
  if (!memory?.usedJSHeapSize) {
    return null;
  }
  return memory.usedJSHeapSize / 1024 / 1024;
}

function formatHeapUsed(heapUsedMb: number | null): string {
  return heapUsedMb === null ? "-" : `${heapUsedMb.toFixed(0)}MB`;
}

function formatDebugToggle(enabled: boolean): string {
  return enabled ? "on" : "off";
}

function createEmptyStoneRenderStats(): StoneRenderStats {
  return {
    simple: 0,
    medium: 0,
    full: 0,
  };
}

function isTextInputActive(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }
  const tagName = activeElement.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    (activeElement as HTMLElement).isContentEditable
  );
}

function getCultivatorVisualGrowth(level: number): number {
  if (!Number.isFinite(level) || level <= 1) {
    return 0;
  }
  const normalized = Math.max(1, Math.min(50, Math.floor(level)));
  const realmIndex = Math.floor((normalized - 1) / 10);
  const layerIndex = (normalized - 1) % 10;
  return realmIndex + layerIndex / 9;
}
